/** GameplayScreen — Dragon Splash: Territory Puzzle のゲームプレイ画面。
 *  Reference: docs/CLAUDE_CODE_UI_IMPLEMENTATION_BRIEF.md §4.3
 *  - World は viewport より広い(60x60 セル)。 Camera がプレイヤーを追従する。
 *  - 上部 HUD: Paint XX% / mm:ss / #N / 歯車
 *  - 下部: 半透明ジョイスティック(4 方向 DOM ボタン)
 *  - 既存の Player / Trail / Territory / Enemy / StageState / WinOverlay /
 *    EoRFailureOverlay / LoopClosedBurst / PointerInput / KeyboardInput を流用
 */

import { Application, Container, Graphics, type Ticker } from 'pixi.js';
import { Player } from '../../core/Player';
import { Trail } from '../../core/Trail';
import { Territory } from '../../core/Territory';
import { Enemy } from '../../core/Enemy';
import { gridEquals, DIRECTION_DELTA, type Direction, type GridConfig } from '../../core/types';
import { attachPointerInput } from '../../input/PointerInput';
import { attachKeyboardInput } from '../../input/KeyboardInput';
import { StageState } from '../../game/StageState';
import {
  StageLoader,
  type StageJson,
  type ObstacleRect,
  type Collectible,
  type SpecialZone,
  type TeleporterSpec,
} from '../../game/StageLoader';
import { PaintingFragmentStore } from '../../game/PaintingFragmentStore';
import { getArtNote } from '../../game/ArtNotes';
import { InventoryStore, HELPER_META, HELPER_IDS, type HelperId } from '../../game/InventoryStore';

/** プレイ前 Booster Pouch から渡される選択。 */
export interface ActiveBoosters {
  readonly brushBoost: boolean; // 開幕 3 秒、速度 1.5×
  readonly bigStart: boolean;   // 開始陣地 5×5 → 7×7
  readonly extraLife: boolean;  // 失敗時に 1 回追加チャンス
}
import { WinOverlay } from '../WinOverlay';
import { EoRFailureOverlay } from '../EoRFailureOverlay';
import { LoopClosedBurst } from '../LoopClosedBurst';
import { PIXI_COLORS } from '../theme';

export interface GameplayScreenOptions {
  readonly onBack: () => void;
  /** ステージクリア時に呼ばれる(設定すれば main.ts 側で次ステージ進行を制御) */
  readonly onStageClear?: (stageId: string, finalCoverage: number) => void;
  /** 現在の Color 残高(EoR の動的表示に使う) */
  readonly getColorBalance?: () => number;
  /** 現在のハート残数(EoR Continue with Heart の表示・有効化判定に使う) */
  readonly getHearts?: () => number;
  /** EoR Continue: Watch Ad → Promise<true で続行 / false で諦め>。main.ts が AdMockOverlay を駆動。 */
  readonly onContinueWithAd?: () => Promise<boolean>;
  /** EoR Continue: 1 ハート消費して続行(true で成功・ハート消費済み) */
  readonly onContinueWithHeart?: () => boolean;
  /** EoR Continue: Color 価格を消費して続行(900 Color、true で成功) */
  readonly onContinueWithColor?: () => boolean;
  /** ポーズメニュー「Reset Progress」押下で呼ばれる */
  readonly onResetProgress?: () => void;
  /** Collectible 取得時に呼ばれる(main.ts で Color or Heart を加算) */
  readonly onCollectibleGained?: (type: 'color_bonus' | 'heart_bonus', value: number) => void;
  /** time_crystal pickup 時に呼ばれる(main.ts 側でタイマー延長を通知したい場合) */
  readonly onTimeCrystalGained?: () => void;
  /** ループ閉鎖ボーナス通知(面積セル数 + 倍率)。main.ts 側で Color 加算などに使用。 */
  readonly onLoopBonus?: (cells: number, multiplier: number) => void;
  /** お助けアイテム 4 種の所持管理(main.ts 側で生成して渡す) */
  readonly inventoryStore?: InventoryStore;
  /** 選択中キャラクターのビジュアル情報(main.ts から characterStore.getSelectedMeta() を渡す) */
  readonly getCharacterMeta?: () => { bodyColor: number; accentColor: number; inkColor: number; creamColor: number; handleColor: number };
  /** 名画ピース拾得時(main.ts から PaintingFragmentStore を渡す) */
  readonly paintingFragmentStore?: PaintingFragmentStore;
}

/** Continue with Color の標準価格(MONETIZATION.md mock 値、後でリモコン化可能) */
export const CONTINUE_COLOR_COST = 900;

const PLAYER_SPEED_CELLS_PER_SEC = 5;
const INITIAL_LIVES = 3;
// VIEW_GRID = viewport に並ぶセル数(zoom 制御)。
// stage が 30x30 でも VIEW_GRID=12 にすると世界が viewport の 2.5 倍 → camera が follow し画面外スクロール可能。
const VIEW_GRID = 12;
const CAMERA_FOLLOW_LERP = 0.18; // 1 = instant snap, lower = smoother trailing
const PLAYER_ROTATE_LERP = 0.25;
const DEFAULT_STAGE_ID = '1-01';
// JSON に speed / direction_change_chance がない時のフォールバック
const DEFAULT_ENEMY_SPEED = 3.0;
const DEFAULT_ENEMY_CHANGE_CHANCE = 0.18;

// B1: 開幕スローイン — 速度を SLOW_IN_START_FACTOR から 1.0 へイージング
const SLOW_IN_DURATION_SEC = 1.4;
const SLOW_IN_START_FACTOR = 0.6;
// B2: カバレッジマイルストーン
const COVERAGE_MILESTONES: readonly number[] = [0.25, 0.5, 0.75];
const MILESTONE_PULSE_MS = 700;
const DIRECTION_TO_RADIANS: Record<Direction, number> = {
  right: 0,
  downRight: Math.PI / 4,
  down: Math.PI / 2,
  downLeft: (3 * Math.PI) / 4,
  left: Math.PI,
  upLeft: -(3 * Math.PI) / 4,
  up: -Math.PI / 2,
  upRight: -Math.PI / 4,
};

/** paper.io 式に「奪える」ライバル領地のテンプレ。
 *  プレイヤーがトレイルで囲む / 通過すると、その領域が territory へ移譲される。
 *  worldGrid >= 20 のステージのみ配置(小さなチュートリアルステージはスキップ)。 */
interface RivalSpec {
  readonly id: string;
  readonly color: number;
  /** worldGrid を受け取り、ステージ内の矩形配置を返す。 */
  readonly placement: (worldGrid: number) => {
    readonly x: number;
    readonly y: number;
    readonly width: number;
    readonly height: number;
  };
}

const RIVAL_TEMPLATES: readonly RivalSpec[] = [
  // ピンク:左上の角ブロック
  { id: 'pink', color: PIXI_COLORS.pink, placement: () => ({ x: 2, y: 2, width: 5, height: 5 }) },
  // ブルー:右下の角ブロック(worldGrid 相対)
  { id: 'blue', color: PIXI_COLORS.blue, placement: (g) => ({ x: g - 7, y: g - 7, width: 5, height: 5 }) },
];

interface LocalEnemySpec {
  readonly spawn: { readonly x: number; readonly y: number };
  readonly speed: number;
  readonly directionChangeChance: number;
  readonly ai: 'random_walk' | 'player_track' | 'trail_target' | 'sleeper' | 'patroller' | 'guard' | 'mirror_echo';
  readonly wakeRadius?: number;
  readonly patrolPath?: readonly { readonly x: number; readonly y: number }[];
  readonly guardAnchor?: { readonly x: number; readonly y: number };
}

export class GameplayScreen {
  private readonly root: HTMLDivElement;
  private readonly onBack: () => void;
  private readonly onStageClear?: (stageId: string, finalCoverage: number) => void;
  private readonly getColorBalanceCb?: () => number;
  private readonly getHeartsCb?: () => number;
  private readonly onContinueWithAdCb?: () => Promise<boolean>;
  private readonly onContinueWithHeartCb?: () => boolean;
  private readonly onContinueWithColorCb?: () => boolean;
  private readonly onResetProgress?: () => void;
  private readonly onCollectibleGainedCb?: (type: 'color_bonus' | 'heart_bonus', value: number) => void;
  private readonly onLoopBonusCb?: (cells: number, multiplier: number) => void;
  private readonly onTimeCrystalGainedCb?: () => void;
  private readonly inventoryStore: InventoryStore;
  private readonly getCharacterMetaCb?: () => { bodyColor: number; accentColor: number; inkColor: number; creamColor: number; handleColor: number };
  private readonly paintingFragmentStore?: PaintingFragmentStore;
  private currentStageId: string = DEFAULT_STAGE_ID;

  // === お助けアイテム状態(class field 化で ticker / tryUseHelper 間で共有) ===
  /** shield_bubble 有効フラグ(collectible 拾得 OR フッターボタンで立つ) */
  private playerHasShield = false;
  /** quick_dash / Quick Dash ヘルパー有効期限(performance.now() ms) */
  private playerDashUntilMs = 0;
  /** Freeze Brush 有効期限: この時刻まで全敵を停止 */
  private enemiesFreezeUntilMs = 0;
  /** 各ヘルパーのクールダウン期限 */
  private helperCooldownUntilMs: Record<HelperId, number> = {
    freeze: 0,
    burst: 0,
    dash: 0,
    shield: 0,
  };
  /** Burst は player/territory/stageState/showResult への参照が必要なので、
   *  start() 内のクロージャをここに保持して tryUseHelper から呼ぶ。 */
  private helperBurstAction: (() => void) | null = null;
  /** start() 〜 stop() の間だけ true。tryUseHelper はこの間のみ動作。 */
  private helperGameplayActive = false;

  private app: Application | null = null;
  private detachKeyboard: (() => void) | null = null;
  private detachPointer: (() => void) | null = null;
  private tickerCallback: ((ticker: Ticker) => void) | null = null;
  /** paper.io 式に奪えるライバル領地。enclose 時にプレイヤーへ移譲される。 */
  private rivalTerritories: Territory[] = [];

  private hudPaint: HTMLElement | null = null;
  private hudTarget: HTMLElement | null = null;
  private hudTime: HTMLElement | null = null;
  private hudLives: HTMLElement | null = null;
  private hudStage: HTMLElement | null = null;
  private hudProgressFill: HTMLElement | null = null;
  private hudScoreValue: HTMLElement | null = null;
  private hudBestValue: HTMLElement | null = null;
  private hudTrailRisk: HTMLElement | null = null;
  /** DOM ポップアップの現在表示数(最大 3 個制限) */
  private loopBonusPopupCount = 0;
  private stageCard: HTMLElement | null = null;
  private stageCardTimeout: number | null = null;
  /** M1: DRAW! キュー要素。スローイン完了時に is-visible を付与、1 秒後に除去。 */
  private stageDrawCue: HTMLElement | null = null;
  private stageDrawCueTimeout: number | null = null;
  private pausePanel: HTMLElement | null = null;
  private currentTargetCoverage: number = 0.45;
  /** ステージ制限時間(秒)。null = タイマー無効(カウントアップ)、数値 = カウントダウン。 */
  private currentTimeLimitSec: number | null = null;

  private elapsedSec = 0;
  private lastMilestoneIndex = 0; // B2: 何個のマイルストーンを越えたか(0..COVERAGE_MILESTONES.length)
  /** mirror_echo: プレイヤー位置の過去 300 フレームリングバッファ */
  private playerHistory: { x: number; y: number }[] = [];
  /** G1: 最後にワープした時刻(performance.now() ms)、クールダウン 1500ms */
  private playerLastTeleportMs = 0;
  private isPauseMenuOpen = false;
  private pauseRequest: (() => void) | null = null;
  private resumeRequest: (() => void) | null = null;
  private detachResize: (() => void) | null = null;
  // F1: Camera Shake state
  private shakeUntilMs = 0;
  private shakeMagnitude = 0;
  // F3: Camera Lookahead smooth state
  private lookaheadCurrentX = 0;
  private lookaheadCurrentY = 0;
  // Combo system
  private comboCount = 0;
  private comboExpireMs = 0;
  private hudCombo: HTMLElement | null = null;

  constructor(root: HTMLDivElement, options: GameplayScreenOptions) {
    this.root = root;
    this.onBack = options.onBack;
    this.onStageClear = options.onStageClear;
    this.getColorBalanceCb = options.getColorBalance;
    this.getHeartsCb = options.getHearts;
    this.onContinueWithAdCb = options.onContinueWithAd;
    this.onContinueWithHeartCb = options.onContinueWithHeart;
    this.onContinueWithColorCb = options.onContinueWithColor;
    this.onResetProgress = options.onResetProgress;
    this.onCollectibleGainedCb = options.onCollectibleGained;
    this.onLoopBonusCb = options.onLoopBonus;
    this.onTimeCrystalGainedCb = options.onTimeCrystalGained;
    this.inventoryStore = options.inventoryStore ?? new InventoryStore();
    this.getCharacterMetaCb = options.getCharacterMeta;
    this.paintingFragmentStore = options.paintingFragmentStore;
    this.renderShell();
  }

  private renderShell(): void {
    // paper.io 2 風レイアウト:
    //   - フィールド(Pixi canvas)が画面全面に展開、木枠なし
    //   - HUD は上部フロート、押下で原画面が透ける
    //   - 移動はタップ&ドラッグ(画面どこでも): PointerInput がすでに angle ベースで動作
    //   - 仮想ジョイスティック DOM は廃止
    this.root.innerHTML = `
      <div class="game-canvas-holder" data-stage></div>

      <!-- Drag Stick(タッチ/マウスの drag-anchor を可視化、操作中だけ is-active) -->
      <div class="game-drag-stick" data-drag-stick aria-hidden="true">
        <div class="game-drag-stick-ring"></div>
        <div class="game-drag-stick-handle" data-drag-stick-handle></div>
      </div>

      <div class="game-hud-top">
        <div class="hud-pill hud-pill--paint">
          <span class="hud-pill-icon" aria-hidden="true">🌼</span>
          <span class="hud-pill-text">
            <strong data-hud-paint>0%</strong>
            <span class="hud-paint-target">/ <span data-hud-target>10%</span></span>
          </span>
        </div>
        <div class="hud-pill hud-pill--time" aria-label="Time remaining">
          <span class="hud-pill-icon" aria-hidden="true">⏱</span>
          <span class="hud-pill-text" data-hud-time>00:00</span>
        </div>
        <button class="hud-gear" type="button" data-action="back" aria-label="Settings / Pause">⚙️</button>
      </div>

      <div class="hud-progress">
        <div class="hud-progress-track" aria-hidden="true">
          <div class="hud-progress-fill" data-hud-progress-fill style="width: 0%"></div>
        </div>
        <div class="hud-stage" aria-live="polite">
          <span class="hud-stage-prefix">STAGE</span>
          <span class="hud-stage-id" data-hud-stage>1-01</span>
        </div>
      </div>

      <!-- スコア・ベスト chip + トレイルリスクインジケーター -->
      <div class="hud-score-row">
        <div class="hud-score" data-hud-score aria-label="Current Color balance">🪙 <span data-hud-score-value>0</span></div>
        <div class="hud-stage-best" data-hud-stage-best aria-label="Stage best coverage">BEST <span data-hud-best-value>-</span></div>
        <div class="trail-risk-indicator" data-trail-risk aria-live="polite" aria-atomic="true"></div>
      </div>

      <!-- コンボ表示(コンボ中のみ visible) -->
      <div class="hud-combo" data-hud-combo aria-live="polite" aria-atomic="true"></div>

      <div class="game-stage-card" data-stage-card aria-live="polite" hidden>
        <div class="stage-card-num" data-stage-card-num>STAGE 1-01</div>
        <div class="stage-card-name" data-stage-card-name>First Brushstroke</div>
      </div>

      <!-- M1: スローイン演出(SLOW_IN_DURATION_SEC)完了後に is-visible を付与して表示。
           CSS は style.css の .stage-draw-cue / .stage-draw-cue.is-visible で定義(Agent B 担当)。 -->
      <div class="stage-draw-cue" data-stage-draw-cue>DRAW!</div>

      <!-- 描き上がる絵画 ミニ canvas:territory/trail/敵/プレイヤー位置を縮小描画。半透明で視界を遮らない。 -->
      <div class="mini-canvas-wrap" data-mini-canvas-wrap aria-hidden="true">
        <canvas class="mini-canvas" data-mini-canvas width="120" height="120"></canvas>
        <div class="mini-canvas-frame-corner mini-canvas-frame-corner--tl" aria-hidden="true"></div>
        <div class="mini-canvas-frame-corner mini-canvas-frame-corner--tr" aria-hidden="true"></div>
        <div class="mini-canvas-frame-corner mini-canvas-frame-corner--bl" aria-hidden="true"></div>
        <div class="mini-canvas-frame-corner mini-canvas-frame-corner--br" aria-hidden="true"></div>
        <div class="mini-canvas-label" data-mini-canvas-label>
          <span class="mini-canvas-pct" data-mini-canvas-pct>0%</span>
          <span class="mini-canvas-target" data-mini-canvas-target>/ 0%</span>
        </div>
      </div>

      <!-- Royal Match 風リザルト失敗 -->
      <div class="eor-continue rm-eor" data-eor-continue aria-hidden="true">
        <div class="rm-eor-card rm-card">
          <div class="rm-eor-banner-wrap">
            <div class="rm-banner rm-banner--red">残念！</div>
          </div>
          <div class="rm-eor-stage" data-eor-stage>ステージ 1-01</div>
          <div class="rm-eor-coverage-row">
            <span>塗り面積</span>
            <strong data-eor-coverage>0% / 0%</strong>
          </div>
          <div class="rm-eor-progress-bar" aria-hidden="true">
            <!-- M4: class="rm-eor-progress-fill" は CSS スタイル用。
                 JS からの参照は data-eor-progress-fill(data 属性)で行い、class 名変更に耐性を持たせる。 -->
            <div class="rm-eor-progress-fill" data-eor-progress-fill style="width:0%"></div>
          </div>
          <div class="rm-eor-missing" data-eor-missing>あと 0% で完成</div>
          <div class="rm-eor-prompt">もうちょっとだった…続きから挑戦する?</div>
          <div class="rm-eor-options">
            <button class="rm-eor-opt rm-eor-opt--ad" type="button" data-action="eor-ad">
              <span class="rm-eor-opt-icon">📺</span>
              <span class="rm-eor-opt-body">
                <span class="rm-eor-opt-label">動画を見て続ける</span>
                <span class="rm-eor-opt-sub">無料</span>
              </span>
            </button>
            <button class="rm-eor-opt rm-eor-opt--heart" type="button" data-action="eor-heart">
              <span class="rm-eor-opt-icon">❤️</span>
              <span class="rm-eor-opt-body">
                <span class="rm-eor-opt-label">ハートで続ける</span>
                <span class="rm-eor-opt-sub" data-eor-heart-sub>残り 0 個</span>
              </span>
            </button>
            <button class="rm-eor-opt rm-eor-opt--color" type="button" data-action="eor-color">
              <span class="rm-eor-opt-icon">🪙</span>
              <span class="rm-eor-opt-body">
                <span class="rm-eor-opt-label">900 Color で続ける</span>
                <span class="rm-eor-opt-sub" data-eor-color-sub>0 / 900</span>
              </span>
            </button>
          </div>
          <button class="rm-cta-secondary rm-eor-exit" type="button" data-action="eor-exit">ステージをやめる</button>
        </div>
      </div>

      <!-- Royal Match 風リザルト成功 -->
      <div class="result-overlay rm-result" data-result-overlay aria-hidden="true">
        <div class="rm-result-card rm-card">
          <div class="result-burst" aria-hidden="true">
            <span class="confetti confetti--1"></span>
            <span class="confetti confetti--2"></span>
            <span class="confetti confetti--3"></span>
            <span class="confetti confetti--4"></span>
            <span class="confetti confetti--5"></span>
            <span class="confetti confetti--6"></span>
          </div>
          <div class="rm-result-banner-wrap">
            <div class="rm-banner">STAGE CLEAR!</div>
          </div>
          <div class="rm-result-stage" data-result-stage>STAGE 1-01</div>
          <div class="rm-result-stars" aria-hidden="true">
            <span class="rm-star" data-result-star="1">★</span>
            <span class="rm-star" data-result-star="2">★</span>
            <span class="rm-star" data-result-star="3">★</span>
          </div>
          <div class="rm-result-coverage">
            <div class="rm-result-coverage-label">COVERAGE</div>
            <div class="rm-result-coverage-value" data-result-coverage>0% / 0%</div>
            <div class="rm-result-progress-bar" aria-hidden="true">
              <!-- M4: class="rm-result-progress-fill" は CSS スタイル用。
                   JS からの参照は data-result-progress-fill(data 属性)で行い、class 名変更に耐性を持たせる。 -->
              <div class="rm-result-progress-fill" data-result-progress-fill style="width:0%"></div>
            </div>
          </div>
          <div class="rm-result-reward">
            <span class="rm-result-reward-icon">🪙</span>
            <span class="rm-result-reward-plus">+</span>
            <span class="rm-result-reward-value" data-result-reward>0</span>
          </div>
          <div class="rm-result-actions">
            <button class="rm-cta-primary" type="button" data-action="result-next">NEXT</button>
            <div class="rm-result-sub-actions">
              <button class="rm-cta-secondary" type="button" data-action="result-replay">↻ Replay</button>
              <button class="rm-cta-secondary" type="button" data-action="result-home">🏠 Home</button>
            </div>
          </div>
        </div>
      </div>

      <div class="game-pause-panel" data-pause-panel aria-hidden="true">
        <div class="pause-card">
          <div class="pause-title">PAUSED</div>
          <button class="pause-btn pause-btn--resume" type="button" data-action="resume">▶ Resume</button>
          <button class="pause-btn pause-btn--home" type="button" data-action="back-home">🏠 Home</button>
          <button class="pause-btn pause-btn--reset" type="button" data-action="reset-progress">↺ Reset Progress</button>
        </div>
      </div>

      <!-- お助けアイテムフッター: 4 種(❄️ Freeze / 🎨 Burst / ⚡ Dash / 🛡️ Shield) -->
      <div class="game-footer" data-helper-footer>
        <button class="helper-btn" type="button" data-helper="freeze" data-count="0" aria-label="${HELPER_META.freeze.name}: ${HELPER_META.freeze.descJa}">
          <span class="helper-icon"><img class="helper-icon-img" data-helper-icon="freeze" alt="" /><span class="helper-icon-fallback">${HELPER_META.freeze.icon}</span></span>
          <span class="helper-label">Freeze</span>
          <span class="helper-badge" data-helper-count="freeze">0</span>
        </button>
        <button class="helper-btn" type="button" data-helper="burst" data-count="0" aria-label="${HELPER_META.burst.name}: ${HELPER_META.burst.descJa}">
          <span class="helper-icon"><img class="helper-icon-img" data-helper-icon="burst" alt="" /><span class="helper-icon-fallback">${HELPER_META.burst.icon}</span></span>
          <span class="helper-label">Burst</span>
          <span class="helper-badge" data-helper-count="burst">0</span>
        </button>
        <button class="helper-btn" type="button" data-helper="dash" data-count="0" aria-label="${HELPER_META.dash.name}: ${HELPER_META.dash.descJa}">
          <span class="helper-icon"><img class="helper-icon-img" data-helper-icon="dash" alt="" /><span class="helper-icon-fallback">${HELPER_META.dash.icon}</span></span>
          <span class="helper-label">Dash</span>
          <span class="helper-badge" data-helper-count="dash">0</span>
        </button>
        <button class="helper-btn" type="button" data-helper="shield" data-count="0" aria-label="${HELPER_META.shield.name}: ${HELPER_META.shield.descJa}">
          <span class="helper-icon"><img class="helper-icon-img" data-helper-icon="shield" alt="" /><span class="helper-icon-fallback">${HELPER_META.shield.icon}</span></span>
          <span class="helper-label">Shield</span>
          <span class="helper-badge" data-helper-count="shield">0</span>
        </button>
      </div>
    `;
    this.bindHelperIconImages();
    this.bindHelperButtons();
    this.updateHelperBadges();

    this.hudPaint = this.root.querySelector<HTMLElement>('[data-hud-paint]');
    this.hudTarget = this.root.querySelector<HTMLElement>('[data-hud-target]');
    this.hudTime = this.root.querySelector<HTMLElement>('[data-hud-time]');
    this.hudLives = this.root.querySelector<HTMLElement>('[data-hud-lives]');
    this.hudStage = this.root.querySelector<HTMLElement>('[data-hud-stage]');
    this.hudProgressFill = this.root.querySelector<HTMLElement>('[data-hud-progress-fill]');
    this.hudScoreValue = this.root.querySelector<HTMLElement>('[data-hud-score-value]');
    this.hudBestValue = this.root.querySelector<HTMLElement>('[data-hud-best-value]');
    this.hudTrailRisk = this.root.querySelector<HTMLElement>('[data-trail-risk]');
    this.hudCombo = this.root.querySelector<HTMLElement>('[data-hud-combo]');
    this.stageCard = this.root.querySelector<HTMLElement>('[data-stage-card]');
    this.stageDrawCue = this.root.querySelector<HTMLElement>('[data-stage-draw-cue]');
    this.pausePanel = this.root.querySelector<HTMLElement>('[data-pause-panel]');

    // C1: 歯車は「ゲーム中ならポーズ / 未開始なら Home」
    this.root
      .querySelector<HTMLButtonElement>('[data-action="back"]')
      ?.addEventListener('click', () => {
        if (this.app && this.pauseRequest) {
          this.togglePauseMenu();
        } else {
          void this.stop();
          this.onBack();
        }
      });

    // C1: ポーズパネルの Resume / Home
    this.root
      .querySelector<HTMLButtonElement>('[data-action="resume"]')
      ?.addEventListener('click', () => {
        this.togglePauseMenu(false);
      });
    this.root
      .querySelector<HTMLButtonElement>('[data-action="back-home"]')
      ?.addEventListener('click', () => {
        void this.stop();
        this.onBack();
      });

    // Reset Progress: 進行ステートをクリアして 1-01 から再開
    this.root
      .querySelector<HTMLButtonElement>('[data-action="reset-progress"]')
      ?.addEventListener('click', () => {
        const ok = window.confirm('進行状態をリセットして 1-01 からやり直しますか?');
        if (!ok) return;
        this.togglePauseMenu(false);
        if (this.onResetProgress) {
          this.onResetProgress();
        } else {
          // フォールバック: callback 未設定なら自身で localStorage を消すだけ
          try {
            window.localStorage.removeItem('ck.currentStage');
            window.localStorage.removeItem('ck.clearedStages');
            window.localStorage.removeItem('ck.colorBalance');
          } catch {
            /* noop */
          }
          window.location.reload();
        }
      });
  }

  /** Helper アイコン PNG をフッターに紐づける。
   *  画像ロード失敗時は data 属性を立てて CSS 側で絵文字フォールバックを見せる。 */
  private bindHelperIconImages(): void {
    const iconPaths: Record<HelperId, string> = {
      freeze: '/icons/helpers/freeze.png',
      burst: '/icons/helpers/burst.png',
      dash: '/icons/helpers/dash.png',
      shield: '/icons/helpers/shield.png',
    };
    HELPER_IDS.forEach((id) => {
      const img = this.root.querySelector<HTMLImageElement>(`[data-helper-icon="${id}"]`);
      if (!img) return;
      img.dataset.loaded = 'pending';
      img.addEventListener('load', () => {
        img.dataset.loaded = 'true';
      }, { once: true });
      img.addEventListener('error', () => {
        img.dataset.loaded = 'error';
      }, { once: true });
      img.src = iconPaths[id];
      img.alt = HELPER_META[id].name;
    });
  }

  /** フッター 4 ボタンに click ハンドラを紐づける。 */
  private bindHelperButtons(): void {
    const btns = this.root.querySelectorAll<HTMLButtonElement>('[data-helper]');
    btns.forEach((btn) => {
      const rawId = btn.getAttribute('data-helper');
      if (!rawId) return;
      const id = rawId as HelperId;
      btn.addEventListener('click', (ev) => {
        ev.preventDefault();
        ev.stopPropagation();
        this.tryUseHelper(id);
      });
    });
  }

  /** 残数バッジを更新。shield 中はバッジに ✓ を出す。 */
  private updateHelperBadges(): void {
    HELPER_IDS.forEach((id) => {
      const count = this.inventoryStore.count(id);
      const badge = this.root.querySelector<HTMLElement>(`[data-helper-count="${id}"]`);
      const btn = this.root.querySelector<HTMLButtonElement>(`[data-helper="${id}"]`);
      if (badge) badge.textContent = String(count);
      if (btn) btn.setAttribute('data-count', String(count));
      // shield 発動中は視覚的に ON 表示
      if (id === 'shield' && btn) {
        btn.classList.toggle('is-armed', this.playerHasShield);
      }
    });
  }

  /** お助けアイテム発動の入口。残数チェック → 効果適用 → 演出。 */
  private tryUseHelper(id: HelperId): void {
    if (!this.helperGameplayActive) return;
    const now = performance.now();
    if (this.helperCooldownUntilMs[id] > now) return;
    // shield は既に有効中なら無駄使い禁止
    if (id === 'shield' && this.playerHasShield) {
      this.showHelperToast('シールド有効中');
      return;
    }
    if (this.inventoryStore.count(id) <= 0) {
      this.showHelperToast('お助けが切れています(ステージクリアで補充)');
      return;
    }
    this.inventoryStore.consume(id);
    this.helperCooldownUntilMs[id] = now + 5000;

    switch (id) {
      case 'freeze':
        this.enemiesFreezeUntilMs = now + 3000;
        this.showHelperFlash('rgba(180, 220, 255, 0.40)');
        break;
      case 'burst':
        this.helperBurstAction?.();
        this.showHelperFlash('rgba(255, 196, 47, 0.40)');
        break;
      case 'dash':
        this.playerDashUntilMs = now + 2500;
        this.showHelperFlash('rgba(255, 255, 200, 0.35)');
        break;
      case 'shield':
        this.playerHasShield = true;
        this.showHelperFlash('rgba(255, 210, 255, 0.35)');
        break;
    }
    this.updateHelperBadges();
  }

  /** 発動フラッシュ:画面全体に色を一瞬被せる。 */
  private showHelperFlash(rgba: string): void {
    const flash = document.createElement('div');
    flash.className = 'helper-flash';
    flash.style.background = `radial-gradient(circle at center, ${rgba} 0%, transparent 70%)`;
    document.body.appendChild(flash);
    window.setTimeout(() => {
      flash.remove();
    }, 700);
  }

  /** 短い toast 通知(art-note-toast を流用)。 */
  private showHelperToast(text: string): void {
    let toast = this.root.querySelector<HTMLElement>('.art-note-toast');
    if (!toast) {
      toast = document.createElement('div');
      toast.className = 'art-note-toast';
      document.body.appendChild(toast);
    }
    toast.textContent = text;
    toast.classList.remove('is-visible');
    void toast.offsetWidth;
    toast.classList.add('is-visible');
    window.setTimeout(() => {
      toast?.classList.remove('is-visible');
    }, 2200);
  }

  /** C1: ポーズメニューの表示切替。明示的に show を渡せばその値に強制。 */
  private togglePauseMenu(forceShow?: boolean): void {
    const next = forceShow ?? !this.isPauseMenuOpen;
    if (next === this.isPauseMenuOpen) return;
    this.isPauseMenuOpen = next;
    if (this.pausePanel) {
      this.pausePanel.setAttribute('aria-hidden', String(!next));
      this.pausePanel.classList.toggle('is-open', next);
    }
    if (next) {
      this.pauseRequest?.();
    } else {
      this.resumeRequest?.();
    }
  }

  async start(
    stageId: string = DEFAULT_STAGE_ID,
    boosters: ActiveBoosters = { brushBoost: false, bigStart: false, extraLife: false },
  ): Promise<void> {
    if (this.app) return;

    const stageEl = this.root.querySelector<HTMLDivElement>('[data-stage]');
    if (!stageEl) {
      throw new Error('GameplayScreen: missing [data-stage] container');
    }

    // ステージ JSON 読み込み(失敗時はデフォルトステージにフォールバック)
    let stageJson: StageJson;
    try {
      stageJson = StageLoader.load(stageId);
    } catch (err) {
      console.error('[GameplayScreen] failed to load stage:', stageId, err);
      stageJson = StageLoader.load(DEFAULT_STAGE_ID);
      stageId = DEFAULT_STAGE_ID;
    }
    this.currentStageId = stageId;

    // Lot 1: 自己ベスト chip を初期化
    if (this.hudBestValue) {
      const bestKey = `ck.stageBest.${stageId}`;
      const saved = parseFloat(localStorage.getItem(bestKey) ?? '0');
      this.hudBestValue.textContent = saved > 0 ? `${Math.round(saved * 100)}%` : '-';
    }

    // ステージ JSON から runtime 値を導出
    const worldGrid = stageJson.map.size.width; // assume square; height は使わない
    const playerStartPos = {
      x: stageJson.map.starting_position.x,
      y: stageJson.map.starting_position.y,
    };
    // Booster: bigStart は陣地を ±1 拡張(5×5 → 7×7)
    const bigStartOffset = boosters.bigStart ? 1 : 0;
    const bigStartGrow = boosters.bigStart ? 2 : 0;
    const playerStartTerritory = {
      x: Math.max(0, stageJson.map.starting_territory.x - bigStartOffset),
      y: Math.max(0, stageJson.map.starting_territory.y - bigStartOffset),
      w: stageJson.map.starting_territory.width + bigStartGrow,
      h: stageJson.map.starting_territory.height + bigStartGrow,
    };
    const targetCoverage = stageJson.victory_conditions.primary.value;
    this.currentTargetCoverage = targetCoverage;
    // 構図マスク勝利(Lot 3):mask_cells が定義されていれば「名画の輪郭を塗り切る」勝利モード
    const victoryType = stageJson.victory_conditions.primary.type ?? 'coverage_min';
    const maskCells: readonly { readonly x: number; readonly y: number }[] =
      stageJson.victory_conditions.primary.mask_cells ?? [];
    const hasMaskVictory = victoryType === 'composition_mask' && maskCells.length > 0;
    // ステージ制限時間(null なら旧来のカウントアップ動作)
    this.currentTimeLimitSec = stageJson.constraints.time_limit_sec;
    // ステージ毎にエラpsed と milestone をリセット(連続プレイで old state が残らないよう)
    this.elapsedSec = 0;
    this.lastMilestoneIndex = 0;
    // ステージ識別 UX: 永続チップ + タイトルカード(prefers-reduced-motion 対応は CSS 側で)
    if (this.hudStage) this.hudStage.textContent = stageJson.stage_id;
    this.showStageCard(stageJson.stage_id, stageJson.metadata.title);
    const enemySpecs: readonly LocalEnemySpec[] = stageJson.map.enemies.map((e) => ({
      spawn: { x: e.spawn.x, y: e.spawn.y },
      speed: e.speed ?? DEFAULT_ENEMY_SPEED,
      directionChangeChance: e.direction_change_chance ?? DEFAULT_ENEMY_CHANGE_CHANCE,
      ai: e.ai ?? 'random_walk',
      wakeRadius: e.wake_radius,
      patrolPath: e.patrol_path,
      guardAnchor: e.guard_anchor,
    }));
    // 次フレームで stage サイズが確定するまで待つ(画面切替直後の getBoundingClientRect 対策)。
    // rAF + setTimeout 両走で、ヘッドレス環境(rAF が停止する場合)でも確実に進む。
    await new Promise<void>((resolve) => {
      let done = false;
      const finish = (): void => {
        if (!done) {
          done = true;
          resolve();
        }
      };
      requestAnimationFrame(finish);
      setTimeout(finish, 32);
    });
    const stageBox = stageEl.getBoundingClientRect();
    const viewportW = Math.max(300, Math.round(stageBox.width));
    const viewportH = Math.max(300, Math.round(stageBox.height));

    // cellPx は viewport 幅から逆算(30 セル横並びを基準)
    const cellPx = Math.max(8, Math.floor(viewportW / VIEW_GRID));

    // World grid(stage.map.size.width に依存)
    const grid: GridConfig = {
      gridSize: worldGrid,
      cellPx,
      canvasPx: worldGrid * cellPx,
    };
    // UI オーバーレイ用 grid(WinOverlay/EoR の幅計算で使用、viewport サイズに合わせる)
    const viewportGrid: GridConfig = {
      gridSize: VIEW_GRID,
      cellPx,
      canvasPx: viewportW,
    };

    const app = new Application();
    await app.init({
      width: viewportW,
      height: viewportH,
      // 浮島の周囲は水色(島の外側に出ると camera follow で見える)
      backgroundColor: 0x61c6ff,
      antialias: true,
      autoDensity: true,
      resolution: window.devicePixelRatio || 1,
      preserveDrawingBuffer: true,
    });
    this.app = app;

    stageEl.replaceChildren(app.canvas);

    app.canvas.tabIndex = 0;
    app.canvas.style.outline = 'none';

    const stage = app.stage;

    // World container — 60x60 全体をホスト、カメラ追従で位置を更新
    const world = new Container();
    stage.addChild(world);

    // 紙の質感(world 全体)
    world.addChild(this.buildPaperGrid(grid));

    // === Special Zones — paper 直上、territory より下 ===
    const zones: readonly SpecialZone[] = stageJson.map.special_zones;
    if (zones.length > 0) {
      const zoneLayer = new Container();
      for (const z of zones) {
        const g = new Graphics();
        const zx = z.x * cellPx;
        const zy = z.y * cellPx;
        const zw = z.width * cellPx;
        const zh = z.height * cellPx;
        g.rect(zx, zy, zw, zh);
        switch (z.type) {
          case 'speed_boost':
            g.fill({ color: 0x61c6ff, alpha: 0.22 });
            g.stroke({ width: 2, color: 0x0472c8, alpha: 0.55 });
            break;
          case 'wet_paint':
            g.fill({ color: 0xa094c8, alpha: 0.22 });
            g.stroke({ width: 2, color: 0x5a4a99, alpha: 0.55 });
            // shimmer dots
            for (let di = 0; di < 6; di++) {
              const sx = zx + (di * 0.17 + 0.08) * zw;
              const sy = zy + ((di % 3) * 0.3 + 0.15) * zh;
              g.circle(sx, sy, Math.max(2, cellPx * 0.08));
              g.fill({ color: 0xd0c8ff, alpha: 0.45 });
            }
            break;
          case 'honey_trap':
            g.fill({ color: 0xf5b942, alpha: 0.28 });
            g.stroke({ width: 2, color: 0xb87820, alpha: 0.6 });
            // diagonal stripes
            for (let si = -4; si < 12; si++) {
              const sx = zx + (si / 10) * zw;
              g.moveTo(sx, zy).lineTo(sx + zh * 0.4, zy + zh);
              g.stroke({ width: Math.max(1.5, cellPx * 0.06), color: 0xe8a020, alpha: 0.28, cap: 'round' });
            }
            break;
          case 'wind_stream': {
            g.fill({ color: 0x88ccff, alpha: 0.16 });
            g.stroke({ width: 2, color: 0x3399ee, alpha: 0.5 });
            // chevron arrows in direction
            const dir = z.direction ?? 'right';
            const cx = zx + zw / 2;
            const cy = zy + zh / 2;
            const aw = Math.min(zw, zh) * 0.28;
            const offsets = [-0.3, 0, 0.3];
            for (const off of offsets) {
              const ox = dir === 'left' || dir === 'right' ? cx + off * zw * 0.55 : cx;
              const oy = dir === 'up' || dir === 'down' ? cy + off * zh * 0.55 : cy;
              if (dir === 'right') {
                g.moveTo(ox - aw * 0.5, oy - aw * 0.5).lineTo(ox + aw * 0.5, oy).lineTo(ox - aw * 0.5, oy + aw * 0.5);
              } else if (dir === 'left') {
                g.moveTo(ox + aw * 0.5, oy - aw * 0.5).lineTo(ox - aw * 0.5, oy).lineTo(ox + aw * 0.5, oy + aw * 0.5);
              } else if (dir === 'down') {
                g.moveTo(ox - aw * 0.5, oy - aw * 0.5).lineTo(ox, oy + aw * 0.5).lineTo(ox + aw * 0.5, oy - aw * 0.5);
              } else {
                g.moveTo(ox - aw * 0.5, oy + aw * 0.5).lineTo(ox, oy - aw * 0.5).lineTo(ox + aw * 0.5, oy + aw * 0.5);
              }
              g.stroke({ width: Math.max(2, cellPx * 0.1), color: 0x3399ee, alpha: 0.6, cap: 'round', join: 'round' });
            }
            break;
          }
          case 'color_pool': {
            // 虹グラデ: 複数の半透明円を重ねて疑似グラデ
            const colors = [0xff4444, 0xff9900, 0xffee00, 0x44cc44, 0x2299ff, 0x9933ff];
            const pcx = zx + zw / 2;
            const pcy = zy + zh / 2;
            const pr = Math.min(zw, zh) / 2;
            for (let ci = 0; ci < colors.length; ci++) {
              const angle = (ci / colors.length) * Math.PI * 2;
              g.circle(pcx + Math.cos(angle) * pr * 0.38, pcy + Math.sin(angle) * pr * 0.38, pr * 0.55);
              g.fill({ color: colors[ci]!, alpha: 0.13 });
            }
            g.rect(zx, zy, zw, zh);
            g.stroke({ width: 2, color: 0xdd88ff, alpha: 0.55 });
            break;
          }
          case 'mirror_canvas':
            g.fill({ color: 0xccddee, alpha: 0.18 });
            g.stroke({ width: 2, color: 0x99aabb, alpha: 0.6 });
            // center X-axis line
            g.moveTo(zx, zy + zh / 2).lineTo(zx + zw, zy + zh / 2);
            g.stroke({ width: Math.max(1.5, cellPx * 0.07), color: 0xaabbcc, alpha: 0.4, cap: 'round' });
            break;
          case 'ripple_zone': {
            // Chapter 2 G1: 水面のリップル。ペールブルー+同心円
            g.fill({ color: 0xb8d4e8, alpha: 0.22 });
            g.stroke({ width: 2, color: 0x6699cc, alpha: 0.6 });
            const rcx = zx + zw / 2;
            const rcy = zy + zh / 2;
            const rMax = Math.min(zw, zh) / 2;
            for (const k of [0.35, 0.6, 0.85]) {
              g.circle(rcx, rcy, rMax * k);
              g.stroke({ width: Math.max(1, cellPx * 0.05), color: 0x88bbdd, alpha: 0.45 });
            }
            break;
          }
          default:
            // slow_zone fallback
            g.fill({ color: 0x8a3ee8, alpha: 0.18 });
            g.stroke({ width: 2, color: 0x4d2295, alpha: 0.55 });
        }
        zoneLayer.addChild(g);
      }
      world.addChild(zoneLayer);
    }

    // ライバル領地(paper.io 式に奪える敵エリア)。
    // ★ 敵がいるステージにのみ、敵の数だけ配置する。
    //   (敵 0 のステージにピンク領地が残るのを防ぐ)
    // worldGrid >= 20 のステージのみ配置。プレイヤー領地より先に world へ乗せ、
    // プレイヤー領地が上に重なって描かれるようにする。
    this.rivalTerritories = [];
    const rivalCount = Math.min(enemySpecs.length, RIVAL_TEMPLATES.length);
    if (worldGrid >= 20 && rivalCount > 0) {
      for (let i = 0; i < rivalCount; i++) {
        const tpl = RIVAL_TEMPLATES[i]!;
        const rect = tpl.placement(worldGrid);
        // ステージ範囲内に収まる場合のみ配置
        if (rect.x < 0 || rect.y < 0) continue;
        if (rect.x + rect.width > worldGrid || rect.y + rect.height > worldGrid) continue;
        const rivalT = new Territory(grid, tpl.color);
        rivalT.setRect(rect.x, rect.y, rect.width, rect.height);
        // paper.io 式レース構造:ライバル領地がじわじわ拡張する
        // 速度はステージ JSON で指定可能(未指定なら 3.0 秒に 1 マス)
        rivalT.enableAutoExpand(stageJson.rival_expand_interval_sec ?? 3.0, 1);
        world.addChild(rivalT.view);
        this.rivalTerritories.push(rivalT);
      }
    }

    // 構図マスク描画(Lot 3):「ここを塗る」を薄色で可視化。プレイヤー領地の下に置く。
    if (hasMaskVictory) {
      const maskGraphics = new Graphics();
      for (const c of maskCells) {
        if (c.x < 0 || c.x >= worldGrid || c.y < 0 || c.y >= worldGrid) continue;
        maskGraphics.rect(c.x * cellPx, c.y * cellPx, cellPx, cellPx);
      }
      maskGraphics.fill({ color: 0xff4f9a, alpha: 0.18 });
      maskGraphics.stroke({ width: 1, color: 0xc81f6e, alpha: 0.35 });
      world.addChild(maskGraphics);
    }

    // プレイヤー領地(オレンジ)
    const territory = new Territory(grid, PIXI_COLORS.orange);
    world.addChild(territory.view);

    const trail = new Trail(grid, PIXI_COLORS.trailEdge);
    world.addChild(trail.view);

    const loopBurst = new LoopClosedBurst(grid, PIXI_COLORS.orange);
    world.addChild(loopBurst.view);

    const player = new Player({
      grid,
      initialPos: playerStartPos,
      color: PIXI_COLORS.orangeDark,
      speedCellsPerSec: PLAYER_SPEED_CELLS_PER_SEC,
      characterMeta: this.getCharacterMetaCb?.(),
    });

    // === Obstacles(壁=進入不可セル、Dried Paint)===
    // color_gate: 初期は obstacleCells に追加しない(updateColorGates が管理)
    // sliding_wall: 初期は obstacleCells に追加しない(毎フレーム slidingObstacleCells で管理)
    const obstacleCells = new Set<string>();
    const obstacles: readonly ObstacleRect[] = stageJson.map.obstacles;
    if (obstacles.length > 0) {
      const obstacleLayer = new Container();
      for (const ob of obstacles) {
        // sliding_wall と color_gate はここでは描画しない(専用レイヤーで管理)
        if (ob.type === 'sliding_wall' || ob.type === 'color_gate') continue;
        const g = new Graphics();
        g.roundRect(
          ob.x * cellPx + 2,
          ob.y * cellPx + 2,
          ob.width * cellPx - 4,
          ob.height * cellPx - 4,
          Math.max(4, cellPx * 0.18),
        );
        // 乾いた絵の具:茶色 + ひび割れたエッジ風 stroke
        g.fill({ color: 0x6b4a2a });
        g.stroke({ width: 2.5, color: 0x3a2210, alpha: 0.85 });
        obstacleLayer.addChild(g);
        // 占有セルを Set 化(O(1) 判定)
        for (let dy = 0; dy < ob.height; dy++) {
          for (let dx = 0; dx < ob.width; dx++) {
            obstacleCells.add(`${ob.x + dx},${ob.y + dy}`);
          }
        }
      }
      world.addChild(obstacleLayer);
    }

    // === G1: Teleporters ===
    /** cell key → destination GridPos(双方向) */
    const teleporterMap = new Map<string, { x: number; y: number }>();
    const teleporterSpecs: readonly TeleporterSpec[] = stageJson.map.teleporters ?? [];
    if (teleporterSpecs.length > 0) {
      const teleporterLayer = new Container();
      for (const tp of teleporterSpecs) {
        // 双方向登録
        teleporterMap.set(`${tp.a.x},${tp.a.y}`, { x: tp.b.x, y: tp.b.y });
        teleporterMap.set(`${tp.b.x},${tp.b.y}`, { x: tp.a.x, y: tp.a.y });
        // 視覚: 渦巻きエフェクト(SVG 円 + 紫グラデ風)
        for (const pos of [tp.a, tp.b]) {
          const g = new Graphics();
          const tx = (pos.x + 0.5) * cellPx;
          const ty = (pos.y + 0.5) * cellPx;
          const r = cellPx * 0.48;
          g.circle(tx, ty, r).fill({ color: 0x7722cc, alpha: 0.35 });
          g.circle(tx, ty, r).stroke({ width: 3, color: 0xcc66ff, alpha: 0.85 });
          g.circle(tx, ty, r * 0.55).fill({ color: 0x9944ff, alpha: 0.4 });
          g.circle(tx, ty, r * 0.28).fill({ color: 0xeeccff, alpha: 0.65 });
          teleporterLayer.addChild(g);
        }
      }
      world.addChild(teleporterLayer);
    }

    // === G3: Sliding Walls ===
    interface SlidingWallState {
      readonly spec: ObstacleRect;
      readonly startMs: number;
      currentX: number;
      currentY: number;
      gfx: Graphics;
    }
    const slidingWalls: SlidingWallState[] = [];
    const slidingWallLayer = new Container();
    const slidingObstacleCells = new Set<string>();
    for (const ob of stageJson.map.obstacles) {
      if (ob.type !== 'sliding_wall') continue;
      const g = new Graphics();
      slidingWalls.push({
        spec: ob,
        startMs: performance.now(),
        currentX: ob.x,
        currentY: ob.y,
        gfx: g,
      });
      slidingWallLayer.addChild(g);
    }
    if (slidingWalls.length > 0) world.addChild(slidingWallLayer);

    // === G2: Color Gates — container for gate graphics (redrawn each frame coverage changes) ===
    const colorGateSpecs = stageJson.map.obstacles.filter((ob) => ob.type === 'color_gate');
    const colorGateLayer = new Container();
    const colorGateGfxMap = new Map<string, Graphics>();
    if (colorGateSpecs.length > 0) {
      for (const gate of colorGateSpecs) {
        const g = new Graphics();
        colorGateGfxMap.set(`${gate.x},${gate.y}`, g);
        colorGateLayer.addChild(g);
      }
      world.addChild(colorGateLayer);
    }
    let colorGateLastCoverage = -1; // force initial draw

    // Helper: rebuild color gate cells and graphics based on current coverage
    const updateColorGates = (coverage: number): void => {
      if (Math.abs(coverage - colorGateLastCoverage) < 0.001) return;
      colorGateLastCoverage = coverage;
      for (const gate of colorGateSpecs) {
        const key = `${gate.x},${gate.y}`;
        const threshold = gate.coverage_threshold ?? 0.3;
        const isOpen = coverage >= threshold;
        // Update obstacleCells
        for (let dy = 0; dy < gate.height; dy++) {
          for (let dx = 0; dx < gate.width; dx++) {
            const ck = `${gate.x + dx},${gate.y + dy}`;
            if (isOpen) {
              obstacleCells.delete(ck);
            } else {
              obstacleCells.add(ck);
            }
          }
        }
        // Redraw
        const g = colorGateGfxMap.get(key);
        if (!g) continue;
        g.clear();
        const gx = gate.x * cellPx + 2;
        const gy = gate.y * cellPx + 2;
        const gw = gate.width * cellPx - 4;
        const gh = gate.height * cellPx - 4;
        if (isOpen) {
          // 開いた: 緑の枠のみ
          g.roundRect(gx, gy, gw, gh, Math.max(4, cellPx * 0.18));
          g.stroke({ width: 3, color: 0x44cc44, alpha: 0.85 });
        } else {
          // 閉じた: 赤格子
          g.roundRect(gx, gy, gw, gh, Math.max(4, cellPx * 0.18));
          g.fill({ color: 0xcc2222, alpha: 0.45 });
          g.stroke({ width: 2.5, color: 0x881111, alpha: 0.85 });
          // 格子線
          const cols = Math.max(2, gate.width);
          const rows = Math.max(2, gate.height);
          for (let col = 1; col < cols; col++) {
            const lx = gate.x * cellPx + col * cellPx;
            g.moveTo(lx, gate.y * cellPx).lineTo(lx, (gate.y + gate.height) * cellPx);
            g.stroke({ width: 1.5, color: 0xff4444, alpha: 0.55 });
          }
          for (let row = 1; row < rows; row++) {
            const ly = gate.y * cellPx + row * cellPx;
            g.moveTo(gate.x * cellPx, ly).lineTo((gate.x + gate.width) * cellPx, ly);
            g.stroke({ width: 1.5, color: 0xff4444, alpha: 0.55 });
          }
        }
      }
    };
    // Initial color gate state (coverage = 0)
    updateColorGates(0);

    // === Collectibles(取得アイテム、Paint Jar / Gold Leaf)===
    interface ActiveCollectible {
      info: Collectible;
      gfx: Graphics;
    }
    const collectibles = new Map<string, ActiveCollectible>();
    const collectibleSpecs: readonly Collectible[] = stageJson.map.collectibles;
    const collectibleLayer = new Container();
    if (collectibleSpecs.length > 0) {
      for (const c of collectibleSpecs) {
        const g = new Graphics();
        const cx = (c.x + 0.5) * cellPx;
        const cy = (c.y + 0.5) * cellPx;
        const r = cellPx * 0.36;
        switch (c.type) {
          case 'color_bonus':
            // 金箔/コイン: gold + ハイライト
            g.circle(cx, cy, r).fill({ color: 0xffc42f });
            g.circle(cx, cy, r).stroke({ width: 2.5, color: 0xc87412, alpha: 0.85 });
            g.circle(cx - r * 0.3, cy - r * 0.3, r * 0.3).fill({ color: 0xfff5b3, alpha: 0.7 });
            break;
          case 'heart_bonus':
            // Heart bonus: 赤系
            g.circle(cx, cy, r).fill({ color: 0xf0697b });
            g.circle(cx, cy, r).stroke({ width: 2.5, color: 0xb53444, alpha: 0.85 });
            g.circle(cx - r * 0.3, cy - r * 0.3, r * 0.3).fill({ color: 0xffd6df, alpha: 0.7 });
            break;
          case 'time_crystal':
            // シアン菱形
            g.moveTo(cx, cy - r).lineTo(cx + r * 0.7, cy).lineTo(cx, cy + r).lineTo(cx - r * 0.7, cy).closePath()
              .fill({ color: 0x22eeff });
            g.moveTo(cx, cy - r).lineTo(cx + r * 0.7, cy).lineTo(cx, cy + r).lineTo(cx - r * 0.7, cy).closePath()
              .stroke({ width: 2, color: 0x0099bb, alpha: 0.85 });
            g.circle(cx - r * 0.2, cy - r * 0.25, r * 0.22).fill({ color: 0xffffff, alpha: 0.6 });
            break;
          case 'shield_bubble':
            // 虹色泡: 3 重リング
            g.circle(cx, cy, r).fill({ color: 0x88ddff, alpha: 0.3 });
            g.circle(cx, cy, r).stroke({ width: 3, color: 0x44aaff, alpha: 0.7 });
            g.circle(cx, cy, r * 0.7).stroke({ width: 2, color: 0xff88cc, alpha: 0.55 });
            g.circle(cx, cy, r * 0.42).stroke({ width: 1.5, color: 0xffdd44, alpha: 0.5 });
            g.circle(cx - r * 0.28, cy - r * 0.32, r * 0.16).fill({ color: 0xffffff, alpha: 0.65 });
            break;
          case 'coverage_token':
            // ローズゴールド六角形
            {
              const hex: number[] = [];
              for (let i = 0; i < 6; i++) {
                const a = (i / 6) * Math.PI * 2 - Math.PI / 6;
                hex.push(cx + Math.cos(a) * r, cy + Math.sin(a) * r);
              }
              g.poly(hex).fill({ color: 0xdd9966 });
              g.poly(hex).stroke({ width: 2.5, color: 0xaa5533, alpha: 0.8 });
              g.circle(cx - r * 0.22, cy - r * 0.22, r * 0.2).fill({ color: 0xffddcc, alpha: 0.65 });
            }
            break;
          case 'quick_dash':
            // 雷アイコン(ジグザグ)
            g.moveTo(cx + r * 0.25, cy - r)
              .lineTo(cx - r * 0.1, cy - r * 0.05)
              .lineTo(cx + r * 0.15, cy - r * 0.05)
              .lineTo(cx - r * 0.25, cy + r)
              .lineTo(cx + r * 0.1, cy + r * 0.05)
              .lineTo(cx - r * 0.15, cy + r * 0.05)
              .closePath()
              .fill({ color: 0xffee22 });
            g.moveTo(cx + r * 0.25, cy - r)
              .lineTo(cx - r * 0.1, cy - r * 0.05)
              .lineTo(cx + r * 0.15, cy - r * 0.05)
              .lineTo(cx - r * 0.25, cy + r)
              .lineTo(cx + r * 0.1, cy + r * 0.05)
              .lineTo(cx - r * 0.15, cy + r * 0.05)
              .closePath()
              .stroke({ width: 1.5, color: 0xcc8800, alpha: 0.8 });
            break;
          case 'art_note':
            // 巻物: 細長い四角 + 上下丸め
            g.roundRect(cx - r * 0.42, cy - r * 0.72, r * 0.84, r * 1.44, r * 0.2)
              .fill({ color: 0xfff5dc });
            g.roundRect(cx - r * 0.42, cy - r * 0.72, r * 0.84, r * 1.44, r * 0.2)
              .stroke({ width: 2, color: 0xb8860b, alpha: 0.85 });
            // 横線 3 本(テキスト)
            for (let li = 0; li < 3; li++) {
              const ly = cy - r * 0.35 + li * r * 0.35;
              g.moveTo(cx - r * 0.28, ly).lineTo(cx + r * 0.28, ly);
              g.stroke({ width: Math.max(1, cellPx * 0.04), color: 0x8b6914, alpha: 0.6, cap: 'round' });
            }
            break;
          case 'hourglass':
            // G5: 金色砂時計
            g.moveTo(cx - r * 0.7, cy - r).lineTo(cx + r * 0.7, cy - r)
              .lineTo(cx, cy).lineTo(cx + r * 0.7, cy + r)
              .lineTo(cx - r * 0.7, cy + r).lineTo(cx, cy).closePath()
              .fill({ color: 0xffc42f });
            g.moveTo(cx - r * 0.7, cy - r).lineTo(cx + r * 0.7, cy - r)
              .lineTo(cx, cy).lineTo(cx + r * 0.7, cy + r)
              .lineTo(cx - r * 0.7, cy + r).lineTo(cx, cy).closePath()
              .stroke({ width: 2, color: 0xc87412, alpha: 0.85 });
            g.circle(cx, cy, r * 0.15).fill({ color: 0xfff5b3, alpha: 0.8 });
            break;
          case 'magnet_pigment':
            // G6: 銀色の磁石(U 字)
            g.arc(cx, cy + r * 0.1, r * 0.65, Math.PI, 0, false)
              .stroke({ width: Math.max(3, cellPx * 0.18), color: 0xaaaacc, alpha: 0.9, cap: 'round' });
            // 左足
            g.rect(cx - r * 0.65 - cellPx * 0.09, cy + r * 0.1, cellPx * 0.18, r * 0.5)
              .fill({ color: 0xff3333 });
            // 右足
            g.rect(cx + r * 0.65 - cellPx * 0.09, cy + r * 0.1, cellPx * 0.18, r * 0.5)
              .fill({ color: 0x3333ff });
            break;
          case 'painting_fragment':
            // G7: 琥珀+金枠のパズルピース(角丸四角 + 凸タブ)
            g.roundRect(cx - r * 0.6, cy - r * 0.6, r * 1.2, r * 1.2, r * 0.18)
              .fill({ color: 0xe8a040 });
            g.roundRect(cx - r * 0.6, cy - r * 0.6, r * 1.2, r * 1.2, r * 0.18)
              .stroke({ width: 2.5, color: 0xc07820, alpha: 0.9 });
            // 中央の小さな絵(花形)
            g.circle(cx, cy, r * 0.28).fill({ color: 0xfff5b3, alpha: 0.75 });
            break;
        }
        collectibleLayer.addChild(g);
        collectibles.set(`${c.x},${c.y}`, { info: c, gfx: g });
      }
      world.addChild(collectibleLayer);
    }

    world.addChild(player.view);

    const enemyContainer = new Container();
    world.addChild(enemyContainer);
    let enemies: Enemy[] = [];

    // Booster: extraLife は失敗時のチャンスを +1
    const effectiveInitialLives = INITIAL_LIVES + (boosters.extraLife ? 1 : 0);
    let stageState = new StageState({
      stageId: stageJson.stage_id,
      targetCoverage,
      initialLives: effectiveInitialLives,
      victoryConditionType: victoryType,
      maskTargetValue: targetCoverage,
    });

    // Lot 3 統合:勝利判定の共通ヘルパー。
    // composition_mask 勝利ステージでは mask_cells の塗布率で判定、それ以外は coverage で判定。
    const checkWinAfterCoverageUpdate = (cov: number): boolean => {
      if (hasMaskVictory) {
        let painted = 0;
        for (const c of maskCells) {
          if (territory.contains(c)) painted++;
        }
        const progress = maskCells.length > 0 ? painted / maskCells.length : 0;
        stageState.setMaskProgress(progress);
        return stageState.isMaskWon();
      }
      stageState.setCoverage(cov);
      return stageState.isWon();
    };

    // C1: ポーズ操作を class field から呼べるよう、stageState を参照するクロージャを格納
    this.pauseRequest = () => {
      if (stageState.isPlaying()) stageState.pause();
    };
    this.resumeRequest = () => {
      stageState.resume();
    };

    // オーバーレイは stage に直接乗せる(camera 追従しない)
    const winOverlay = new WinOverlay({
      grid: viewportGrid,
      onContinue: () => {
        // onStageClear が設定されていれば(main.ts 側で次ステージ進行を制御)、
        // 最終 coverage を渡してコールバック。そうでなければ同ステージをリセット。
        if (this.onStageClear) {
          this.onStageClear(this.currentStageId, territory.coverage());
        } else {
          resetStage();
        }
      },
    });
    stage.addChild(winOverlay.view);

    const eorOverlay = new EoRFailureOverlay({
      grid: viewportGrid,
      onContinue: () => {
        trail.clear();
        player.resetTo(playerStartPos);
        respawnEnemies();
        stageState.resume();
      },
      onExit: () => {
        resetStage();
      },
      getColorBalance: this.getColorBalanceCb,
    });
    stage.addChild(eorOverlay.view);

    const respawnEnemies = (): void => {
      enemyContainer.removeChildren();
      enemies = enemySpecs.map((spec) => {
        // AI 種別ごとにカラーを差別化
        const enemyColor =
          spec.ai === 'player_track'
            ? 0xd13a5a
            : spec.ai === 'trail_target'
              ? 0xe07d2a
              : spec.ai === 'patroller'
                ? 0x2277dd
                : spec.ai === 'guard'
                  ? 0xdd2222
                  : spec.ai === 'sleeper'
                    ? 0x888899
                    : spec.ai === 'mirror_echo'
                      ? 0x9988bb
                      : PIXI_COLORS.purple;
        const enemy = new Enemy({
          grid,
          initialPos: spec.spawn,
          color: enemyColor,
          speedCellsPerSec: spec.speed,
          directionChangeChance: spec.directionChangeChance,
          ai: spec.ai,
          getPlayerPos: () => player.getGridPos(),
          getTrailCells: () => trail.getCells(),
          getPlayerHistory: () => this.playerHistory,
          wakeRadius: spec.wakeRadius,
          patrolPath: spec.patrolPath,
          guardAnchor: spec.guardAnchor,
        });
        enemyContainer.addChild(enemy.view);
        return enemy;
      });
    };

    const resetStage = (): void => {
      territory.clear();
      territory.setRect(
        playerStartTerritory.x,
        playerStartTerritory.y,
        playerStartTerritory.w,
        playerStartTerritory.h,
      );
      trail.clear();
      player.resetTo(playerStartPos);
      respawnEnemies();
      stageState = new StageState({
        stageId: stageJson.stage_id,
        targetCoverage,
        initialLives: effectiveInitialLives,
      });
      // pauseRequest/resumeRequest はクロージャで stageState を参照しているため、
      // 新しい stageState への参照を貼り直す
      this.pauseRequest = () => {
        if (stageState.isPlaying()) stageState.pause();
      };
      this.resumeRequest = () => {
        stageState.resume();
      };
      eorOverlay.hide();
      winOverlay.view.visible = false;
      // Result Overlay (HTML) も閉じる
      const ro = this.root.querySelector<HTMLElement>('[data-result-overlay]');
      if (ro) {
        ro.classList.remove('is-open');
        ro.setAttribute('aria-hidden', 'true');
      }
      this.elapsedSec = 0;
      this.lastMilestoneIndex = 0; // B2: マイルストーンリセット
      // Combo reset on stage restart
      this.comboCount = 0;
      this.comboExpireMs = 0;
      if (this.hudCombo) {
        this.hudCombo.textContent = '';
        this.hudCombo.classList.remove('is-active');
      }
      this.togglePauseMenu(false); // C1: ポーズメニュー閉じる
      snapCamera();
      this.updateHud(territory.coverage(), stageState.getLives());
    };

    // 初期化
    territory.setRect(
      playerStartTerritory.x,
      playerStartTerritory.y,
      playerStartTerritory.w,
      playerStartTerritory.h,
    );
    respawnEnemies();

    const handleFailure = (reason: string): void => {
      // shield_bubble: 一度だけ失敗をキャンセルして消費
      if (this.playerHasShield) {
        this.playerHasShield = false;
        this.updateHelperBadges();
        showShieldFlash();
        // F1: reduced shake for shield hit (0.3x magnitude, no hitstop)
        this.shakeMagnitude = cellPx * 0.18;
        this.shakeUntilMs = performance.now() + 220;
        return;
      }
      // F1: Camera Shake — full magnitude on real failure
      this.shakeMagnitude = cellPx * 0.55;
      this.shakeUntilMs = performance.now() + 220;
      // F2: Hitstop — freeze ticker for 50ms on real failure
      app.ticker.speed = 0;
      setTimeout(() => { if (this.app) this.app.ticker.speed = 1.0; }, 50);
      // Audio: stage fail SFX
      window.__ck_sfx?.playSfx('stage_fail');
      // M4: pointerup が来ないままゲームオーバーになった場合に
      // drag stick の is-active が残留するのを明示的にクリア。
      dragStick?.classList.remove('is-active');
      stageState.decrementLives();
      stageState.pause();
      // Pixi 旧 EoR は使わず、HTML EoR Continue ファネル(3 択)を出す。
      // showEoRContinue は後段で定義(closure binding で TDZ 回避済み)。
      showEoRContinue(territory.coverage(), reason);
    };

    // カメラ追従(プレイヤー中心、world の端で clamp、ぬるぬる追従)
    // C3: viewport を毎フレーム app.renderer から読むことで resize 対応
    const worldPxSize = worldGrid * cellPx;
    const LOOKAHEAD_CELLS = 2.5;
    const LOOKAHEAD_LERP = 0.12;
    const computeCameraTarget = (lookaheadX: number, lookaheadY: number): { x: number; y: number } => {
      const vw = app.renderer.width;
      const vh = app.renderer.height;
      const minX = Math.min(0, vw - worldPxSize);
      const minY = Math.min(0, vh - worldPxSize);
      const halfViewX = vw / 2;
      const halfViewY = vh / 2;
      // F3: offset camera target in the direction of travel
      let cx = halfViewX - player.view.x - lookaheadX;
      let cy = halfViewY - player.view.y - lookaheadY;
      cx = Math.max(minX, Math.min(0, cx));
      cy = Math.max(minY, Math.min(0, cy));
      return { x: cx, y: cy };
    };
    const snapCamera = (): void => {
      const t = computeCameraTarget(0, 0);
      world.x = t.x;
      world.y = t.y;
    };
    const stepCamera = (dt: number): void => {
      // F3: smooth lookahead toward player's current direction
      const dir = player.getDirection();
      const delta = DIRECTION_DELTA[dir];
      // 斜め方向は (1,1) のため大きさが √2 になる → 正規化して常に
      // LOOKAHEAD_CELLS 一定にする。これで斜め移動時にカメラが
      // 1.41 倍も過剰に先読みして画面端で乱れるのを防ぐ。
      const dlen = Math.hypot(delta.x, delta.y) || 1;
      const targetLookaheadX = (delta.x / dlen) * LOOKAHEAD_CELLS * cellPx;
      const targetLookaheadY = (delta.y / dlen) * LOOKAHEAD_CELLS * cellPx;
      const lk = 1 - Math.pow(1 - LOOKAHEAD_LERP, dt * 60);
      this.lookaheadCurrentX += (targetLookaheadX - this.lookaheadCurrentX) * lk;
      this.lookaheadCurrentY += (targetLookaheadY - this.lookaheadCurrentY) * lk;

      const t = computeCameraTarget(this.lookaheadCurrentX, this.lookaheadCurrentY);
      const k = 1 - Math.pow(1 - CAMERA_FOLLOW_LERP, dt * 60);
      world.x += (t.x - world.x) * k;
      world.y += (t.y - world.y) * k;

      // F1: Camera Shake — apply offset on top of camera position
      const now = performance.now();
      if (this.shakeUntilMs > now) {
        const tShake = (this.shakeUntilMs - now) / 220; // 1.0 → 0.0
        const magnitude = this.shakeMagnitude * tShake * tShake; // easeOut
        world.x += Math.sin(now * 0.04) * magnitude;
        world.y += Math.cos(now * 0.05) * magnitude;
      }
    };
    const stepPlayerRotation = (dt: number): void => {
      const targetAngle = DIRECTION_TO_RADIANS[player.getDirection()];
      const current = player.view.rotation;
      // 最短経路で回転(-π〜π にラップ)
      let delta = targetAngle - current;
      while (delta > Math.PI) delta -= Math.PI * 2;
      while (delta < -Math.PI) delta += Math.PI * 2;
      const k = 1 - Math.pow(1 - PLAYER_ROTATE_LERP, dt * 60);
      player.view.rotation = current + delta * k;
    };

    // === ミニ canvas:右上に縮小版フィールドを描画(territory/trail/敵/プレイヤー)===
    // paper.io 2 / Hole.io 系の minimap 慣習に Painterly フレームを足した実装。
    // 描画は重くないが毎フレームは過剰なので 4 フレームに 1 回(~15 fps)。
    const miniWrap = this.root.querySelector<HTMLElement>('[data-mini-canvas-wrap]');
    const miniCanvas = this.root.querySelector<HTMLCanvasElement>('[data-mini-canvas]');
    const miniPctEl = this.root.querySelector<HTMLElement>('[data-mini-canvas-pct]');
    const miniTargetEl = this.root.querySelector<HTMLElement>('[data-mini-canvas-target]');
    const miniCtx = miniCanvas?.getContext('2d') ?? null;
    if (miniWrap) {
      miniWrap.setAttribute('aria-hidden', 'false');
    }
    if (miniTargetEl) {
      miniTargetEl.textContent = `/ ${Math.round(targetCoverage * 100)}%`;
    }
    let miniFrameCounter = 0;
    const renderMiniCanvas = (): void => {
      if (!miniCanvas || !miniCtx) return;
      const W = miniCanvas.width;
      const H = miniCanvas.height;
      const G = grid.gridSize;
      const sx = W / G;
      const sy = H / G;
      // 背景(クリーム+わずかな粒状)
      miniCtx.fillStyle = '#fff8e0';
      miniCtx.fillRect(0, 0, W, H);
      // territory:プレイヤーの塗り(ゴールド)
      miniCtx.fillStyle = '#ffcf54';
      for (let y = 0; y < G; y++) {
        for (let x = 0; x < G; x++) {
          if (territory.contains({ x, y })) {
            miniCtx.fillRect(x * sx, y * sy, sx + 0.6, sy + 0.6);
          }
        }
      }
      // trail(進行中の輪郭、橙)
      miniCtx.fillStyle = '#ff8a40';
      for (const c of trail.getCells()) {
        miniCtx.fillRect(c.x * sx, c.y * sy, sx + 0.6, sy + 0.6);
      }
      // obstacles(壁、暗い茶)
      miniCtx.fillStyle = 'rgba(60, 40, 20, 0.85)';
      for (const key of obstacleCells) {
        const [xs, ys] = key.split(',');
        const x = Number(xs);
        const y = Number(ys);
        if (!Number.isFinite(x) || !Number.isFinite(y)) continue;
        miniCtx.fillRect(x * sx, y * sy, sx + 0.5, sy + 0.5);
      }
      // 敵(赤点)
      miniCtx.fillStyle = '#d13a5a';
      for (const e of enemies) {
        const p = e.getGridPos();
        miniCtx.beginPath();
        miniCtx.arc((p.x + 0.5) * sx, (p.y + 0.5) * sy, 2.4, 0, Math.PI * 2);
        miniCtx.fill();
      }
      // プレイヤー(緑点+白縁)
      const pp = player.getGridPos();
      miniCtx.fillStyle = '#3fa830';
      miniCtx.beginPath();
      miniCtx.arc((pp.x + 0.5) * sx, (pp.y + 0.5) * sy, 3.2, 0, Math.PI * 2);
      miniCtx.fill();
      miniCtx.strokeStyle = '#fff';
      miniCtx.lineWidth = 1.6;
      miniCtx.stroke();
      // 現在 coverage を中央下にテキスト
      const covPct = Math.round(territory.coverage() * 100);
      if (miniPctEl) miniPctEl.textContent = `${covPct}%`;
      // ターゲット達成時に "✓" ハイライト
      if (miniWrap) {
        miniWrap.classList.toggle(
          'is-target-reached',
          territory.coverage() >= targetCoverage,
        );
      }
    };

    // === EoR Continue Funnel(HTML 化、3 択:Ad / Heart / Color)===
    const eorContinueOverlay = this.root.querySelector<HTMLElement>('[data-eor-continue]');
    const eorStageEl = this.root.querySelector<HTMLElement>('[data-eor-stage]');
    const eorCoverageEl = this.root.querySelector<HTMLElement>('[data-eor-coverage]');
    const eorMissingEl = this.root.querySelector<HTMLElement>('[data-eor-missing]');
    const eorHeartSub = this.root.querySelector<HTMLElement>('[data-eor-heart-sub]');
    const eorColorSub = this.root.querySelector<HTMLElement>('[data-eor-color-sub]');
    const eorAdBtn = this.root.querySelector<HTMLButtonElement>('[data-action="eor-ad"]');
    const eorHeartBtn = this.root.querySelector<HTMLButtonElement>('[data-action="eor-heart"]');
    const eorColorBtn = this.root.querySelector<HTMLButtonElement>('[data-action="eor-color"]');
    const eorExitBtn = this.root.querySelector<HTMLButtonElement>('[data-action="eor-exit"]');

    const showEoRContinue = (coverage: number, reason: string): void => {
      if (!eorContinueOverlay) return;
      if (eorStageEl) eorStageEl.textContent = `ステージ ${stageJson.stage_id} — ${stageJson.metadata.title}`;
      if (eorCoverageEl)
        eorCoverageEl.textContent = `${(coverage * 100).toFixed(1)}% / ${(targetCoverage * 100).toFixed(0)}%`;
      const missingPct = Math.max(0, (targetCoverage - coverage) * 100).toFixed(1);
      if (eorMissingEl) {
        // 理由を最上部の太字、残り% を下に表示(「なぜ負けたか」を一目で分かる)。
        // reason は handleFailure 側で固定文字列のみ渡されるため XSS リスクなし。
        eorMissingEl.innerHTML =
          `<span class="eor-fail-reason">${reason}</span>` +
          `<span class="eor-fail-remain">あと ${missingPct}% で完成</span>`;
      }

      // プログレスバー更新
      const eorProgressFill = this.root.querySelector<HTMLElement>('[data-eor-progress-fill]');
      if (eorProgressFill) {
        const pct = Math.min(100, (coverage / Math.max(0.001, targetCoverage)) * 100);
        window.setTimeout(() => { eorProgressFill.style.width = `${pct.toFixed(1)}%`; }, 60);
      }

      // 動的な heart / color 残量表示
      const hearts = this.getHeartsCb ? this.getHeartsCb() : 0;
      const colors = this.getColorBalanceCb ? this.getColorBalanceCb() : 0;
      if (eorHeartSub) eorHeartSub.textContent = `残り ${hearts} 個`;
      if (eorColorSub) eorColorSub.textContent = `${colors.toLocaleString('en-US')} / ${CONTINUE_COLOR_COST}`;

      // 各ボタン有効化判定
      if (eorAdBtn) eorAdBtn.disabled = !this.onContinueWithAdCb;
      if (eorHeartBtn) eorHeartBtn.disabled = !this.onContinueWithHeartCb || hearts <= 0;
      if (eorColorBtn)
        eorColorBtn.disabled = !this.onContinueWithColorCb || colors < CONTINUE_COLOR_COST;

      eorContinueOverlay.setAttribute('aria-hidden', 'false');
      eorContinueOverlay.classList.add('is-open');
    };

    const hideEoRContinue = (): void => {
      if (!eorContinueOverlay) return;
      eorContinueOverlay.classList.remove('is-open');
      eorContinueOverlay.setAttribute('aria-hidden', 'true');
    };

    const performContinue = (): void => {
      hideEoRContinue();
      trail.clear();
      player.resetTo(playerStartPos);
      respawnEnemies();
      stageState.restoreForContinue();
      // タイマー復活: Continue ごとに残り時間を満タンに戻す(Royal-Match 仕様)
      this.elapsedSec = 0;
    };

    eorAdBtn?.addEventListener('click', async () => {
      if (!this.onContinueWithAdCb) return;
      eorAdBtn.disabled = true;
      const ok = await this.onContinueWithAdCb();
      if (ok) performContinue();
      else eorAdBtn.disabled = false;
    });
    eorHeartBtn?.addEventListener('click', () => {
      if (!this.onContinueWithHeartCb) return;
      if (this.onContinueWithHeartCb()) performContinue();
    });
    eorColorBtn?.addEventListener('click', () => {
      if (!this.onContinueWithColorCb) return;
      if (this.onContinueWithColorCb()) performContinue();
    });
    eorExitBtn?.addEventListener('click', () => {
      hideEoRContinue();
      resetStage();
    });

    // === Result(リザルト)overlay 表示ハンドラ — Royal-Match 風 HTML 演出 ===
    const resultOverlay = this.root.querySelector<HTMLElement>('[data-result-overlay]');
    const resultCoverage = this.root.querySelector<HTMLElement>('[data-result-coverage]');
    const resultRewardEl = this.root.querySelector<HTMLElement>('[data-result-reward]');
    const resultStageEl = this.root.querySelector<HTMLElement>('[data-result-stage]');
    const resultStars = this.root.querySelectorAll<HTMLElement>('[data-result-star]');

    const showResult = (coverage: number, reward: number): void => {
      // Lot 1: ステージ自己ベスト保存
      const bestKey = `ck.stageBest.${stageId}`;
      const prevBest = parseFloat(localStorage.getItem(bestKey) ?? '0');
      const newBest = Math.max(prevBest, coverage);
      localStorage.setItem(bestKey, String(newBest));
      if (this.hudBestValue) {
        this.hudBestValue.textContent = `${Math.round(newBest * 100)}%`;
      }
      // Audio: stage clear fanfare
      window.__ck_sfx?.playSfx('stage_clear');
      if (!resultOverlay) return;
      if (resultStageEl) {
        resultStageEl.textContent = `STAGE ${stageJson.stage_id} — ${stageJson.metadata.title}`;
      }
      if (resultCoverage) {
        resultCoverage.textContent = `${(coverage * 100).toFixed(1)}% / ${(targetCoverage * 100).toFixed(0)}%`;
      }
      // プログレスバー更新
      const resultProgressFill = this.root.querySelector<HTMLElement>('[data-result-progress-fill]');
      if (resultProgressFill) {
        const pct = Math.min(100, (coverage / Math.max(0.001, targetCoverage)) * 100);
        window.setTimeout(() => { resultProgressFill.style.width = `${pct.toFixed(1)}%`; }, 60);
      }
      // 星評価: target 比 1.0=1星 / 1.2=2星 / 1.5=3星(stages はクリア直後 = target+ε のため大半は 1★)
      const ratio = targetCoverage > 0 ? coverage / targetCoverage : 0;
      const earnedStars = ratio >= 1.5 ? 3 : ratio >= 1.2 ? 2 : 1;
      resultStars.forEach((star, i) => {
        star.classList.remove('is-earned');
        if (i < earnedStars) {
          // 0.18s 刻みで一つずつ点灯
          window.setTimeout(() => star.classList.add('is-earned'), 220 + i * 180);
        }
      });
      // 報酬カウントアップアニメ(0 → reward を 700ms)
      if (resultRewardEl) {
        const start = performance.now();
        const dur = 700;
        const tick = (now: number): void => {
          const t = Math.min(1, (now - start) / dur);
          const eased = 1 - (1 - t) * (1 - t);
          resultRewardEl.textContent = String(Math.round(reward * eased));
          if (t < 1) requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
      }
      resultOverlay.setAttribute('aria-hidden', 'false');
      resultOverlay.classList.add('is-open');
    };

    const hideResult = (): void => {
      if (!resultOverlay) return;
      resultOverlay.classList.remove('is-open');
      resultOverlay.setAttribute('aria-hidden', 'true');
    };

    // === Helper "Color Burst" — 現在地中心 5×5 を territory 化 ===
    this.helperBurstAction = (): void => {
      const p = player.getGridPos();
      const tx = Math.max(0, p.x - 2);
      const ty = Math.max(0, p.y - 2);
      territory.setRect(tx, ty, 5, 5);
      const cov = territory.coverage();
      this.checkMilestone(cov);
      if (checkWinAfterCoverageUpdate(cov)) {
        showResult(cov, stageJson.rewards.color_base);
      }
    };
    this.helperGameplayActive = true;

    // Result ボタンの click は一度バインドしておけば後続でも動く(closure 経由でローカル参照)
    this.root
      .querySelector<HTMLButtonElement>('[data-action="result-next"]')
      ?.addEventListener('click', () => {
        hideResult();
        const cov = territory.coverage();
        if (this.onStageClear) {
          this.onStageClear(this.currentStageId, cov);
        } else {
          resetStage();
        }
      });
    this.root
      .querySelector<HTMLButtonElement>('[data-action="result-replay"]')
      ?.addEventListener('click', () => {
        hideResult();
        resetStage();
      });
    this.root
      .querySelector<HTMLButtonElement>('[data-action="result-home"]')
      ?.addEventListener('click', () => {
        hideResult();
        void this.stop();
        this.onBack();
      });

    // 入力(キーボード + ポインター + DOM ジョイスティック)
    this.detachKeyboard = attachKeyboardInput({
      onDirection: (dir) => {
        if (stageState.isPlaying()) player.setDirection(dir);
      },
      onTogglePause: () => {
        this.togglePauseMenu();
      },
    });
    // === タッチ操作: drag-anchor 式 + 視覚 Drag Stick ===
    const dragStick = this.root.querySelector<HTMLElement>('[data-drag-stick]');
    const dragStickHandle = this.root.querySelector<HTMLElement>('[data-drag-stick-handle]');
    const stageDom = stageEl;
    const MAX_HANDLE_RADIUS = 48; // 視覚ハンドルの表示最大距離 (px)

    this.detachPointer = attachPointerInput({
      canvas: app.canvas,
      onDirection: (dir) => {
        if (stageState.isPlaying()) player.setDirection(dir);
      },
      onDragStart: (cx, cy) => {
        if (!dragStick) return;
        // アンカー位置 = stage 要素ローカル座標
        const rect = stageDom.getBoundingClientRect();
        dragStick.style.left = `${cx - rect.left}px`;
        dragStick.style.top = `${cy - rect.top}px`;
        if (dragStickHandle) {
          dragStickHandle.style.transform = 'translate(-50%, -50%)';
        }
        dragStick.classList.add('is-active');
      },
      onDragMove: (dx, dy, dist) => {
        if (!dragStickHandle) return;
        // 視覚ハンドルは MAX_HANDLE_RADIUS で頭打ち(画面外に飛ばないよう)
        const ratio = dist > MAX_HANDLE_RADIUS ? MAX_HANDLE_RADIUS / dist : 1;
        const hx = dx * ratio;
        const hy = dy * ratio;
        dragStickHandle.style.transform = `translate(calc(-50% + ${hx}px), calc(-50% + ${hy}px))`;
      },
      onDragEnd: () => {
        dragStick?.classList.remove('is-active');
      },
    });

    // === Player buff state ===
    // playerHasShield / playerDashUntilMs / enemiesFreezeUntilMs はクラスフィールドで管理
    // (フッターボタン tryUseHelper と共有するため)
    let playerWideBrushUntilMs = 0;
    // ステージ開始時に既存の buff 状態をリセット
    this.playerHasShield = false;
    this.playerDashUntilMs = 0;
    this.enemiesFreezeUntilMs = 0;
    this.helperCooldownUntilMs = { freeze: 0, burst: 0, dash: 0, shield: 0 };
    // mirror_echo 履歴・テレポーターリセット
    this.playerHistory = [];
    this.playerLastTeleportMs = 0;

    // === Art Note Toast ===
    const showArtNoteToast = (text: string): void => {
      let toast = this.root.querySelector<HTMLElement>('.art-note-toast');
      if (!toast) {
        toast = document.createElement('div');
        toast.className = 'art-note-toast';
        document.body.appendChild(toast);
      }
      toast.textContent = text;
      toast.classList.remove('is-visible');
      // force reflow
      void toast.offsetWidth;
      toast.classList.add('is-visible');
      window.setTimeout(() => toast!.classList.remove('is-visible'), 4000);
    };

    // === Shield Flash ===
    const showShieldFlash = (): void => {
      const el = document.createElement('div');
      el.className = 'shield-flash';
      document.body.appendChild(el);
      window.setTimeout(() => el.remove(), 750);
    };

    const tickerCallback = (ticker: Ticker): void => {
      const dt = ticker.deltaMS / 1000;
      loopBurst.update(dt);
      territory.update(dt);
      for (const rival of this.rivalTerritories) rival.update(dt);

      if (!stageState.isPlaying()) return;

      this.elapsedSec += dt;
      this.updateHud(territory.coverage(), stageState.getLives());

      // Lot 1: スコア chip(Color 残高)更新
      if (this.hudScoreValue && this.getColorBalanceCb) {
        this.hudScoreValue.textContent = this.getColorBalanceCb().toLocaleString('en-US');
      }

      // Lot 1: トレイルリスクインジケーター
      if (this.hudTrailRisk) {
        const trailLen = trail.getCells().length;
        if (trailLen >= 35) {
          this.hudTrailRisk.textContent = 'DANGER';
          this.hudTrailRisk.className = 'trail-risk-indicator is-danger';
        } else if (trailLen >= 20) {
          this.hudTrailRisk.textContent = 'RISK';
          this.hudTrailRisk.className = 'trail-risk-indicator is-warn';
        } else {
          this.hudTrailRisk.textContent = '';
          this.hudTrailRisk.className = 'trail-risk-indicator';
        }
      }

      // Task B: コンボ期限チェック(毎フレーム)
      const nowTick = performance.now();
      if (this.comboCount > 0 && nowTick > this.comboExpireMs) {
        this.comboCount = 0;
        if (this.hudCombo) {
          this.hudCombo.textContent = '';
          this.hudCombo.classList.remove('is-active');
        }
      }

      // === カウントダウン: 時間切れ → ゲームオーバー(EoR Continue 出現)===
      if (
        this.currentTimeLimitSec !== null &&
        this.elapsedSec >= this.currentTimeLimitSec
      ) {
        handleFailure('時間切れ!');
        return;
      }

      // B1: 開幕スローイン — 速度を SLOW_IN_START_FACTOR → 1.0 にイーズアウト
      // Booster brushBoost: 開幕 3 秒は 1.5× オーバーライド(スローイン無視)
      const BRUSH_BOOST_DURATION = 3.0;
      const BRUSH_BOOST_FACTOR = 1.5;
      let speedFactor: number;
      if (boosters.brushBoost && this.elapsedSec < BRUSH_BOOST_DURATION) {
        speedFactor = BRUSH_BOOST_FACTOR;
      } else {
        const tSlow = Math.min(1, this.elapsedSec / SLOW_IN_DURATION_SEC);
        const easedSlow = 1 - (1 - tSlow) * (1 - tSlow);
        speedFactor =
          SLOW_IN_START_FACTOR + (1 - SLOW_IN_START_FACTOR) * easedSlow;
      }

      const now = performance.now();

      // === Special Zone — 現在地が zone 内なら効果適用 ===
      let inColorPool = false;
      let inMirrorCanvas = false;
      let mirrorZone: SpecialZone | null = null;
      let inRippleZone = false;
      let windDx = 0;
      let windDy = 0;

      if (zones.length > 0) {
        const pos = player.getGridPos();
        for (const z of zones) {
          if (
            pos.x >= z.x &&
            pos.x < z.x + z.width &&
            pos.y >= z.y &&
            pos.y < z.y + z.height
          ) {
            switch (z.type) {
              case 'speed_boost':
              case 'slow_zone':
              case 'wet_paint':
              case 'honey_trap':
                speedFactor *= z.multiplier ?? 1.0;
                break;
              case 'wind_stream': {
                const d = z.direction ?? 'right';
                if (d === 'right') windDx += 0.6;
                else if (d === 'left') windDx -= 0.6;
                else if (d === 'down') windDy += 0.6;
                else windDy -= 0.6;
                break;
              }
              case 'color_pool':
                inColorPool = true;
                break;
              case 'mirror_canvas':
                inMirrorCanvas = true;
                mirrorZone = z;
                break;
              case 'ripple_zone':
                inRippleZone = true;
                break;
            }
          }
        }
      }

      // color_pool: wide brush 持続延長
      if (inColorPool) {
        playerWideBrushUntilMs = Math.max(playerWideBrushUntilMs, now + 4000);
      }

      const playerEntered = player.update(dt * speedFactor);
      if (player.hitBoundary) {
        handleFailure('島から落ちた!');
        return;
      }

      // === G4: Mirror Echo — player 位置履歴をリングバッファで蓄積(最大 300 entries) ===
      {
        const pp = player.getGridPos();
        this.playerHistory.push({ x: pp.x, y: pp.y });
        if (this.playerHistory.length > 300) this.playerHistory.shift();
      }

      // === G1: Teleporter 判定 — 新マス進入時のみ ===
      if (playerEntered && teleporterMap.size > 0) {
        const tkey = `${playerEntered.x},${playerEntered.y}`;
        const dest = teleporterMap.get(tkey);
        if (dest) {
          const nowMs = performance.now();
          if (nowMs - this.playerLastTeleportMs >= 1500) {
            this.playerLastTeleportMs = nowMs;
            player.resetTo(dest);
          }
        }
      }

      // === G3: Sliding Wall 更新 ===
      if (slidingWalls.length > 0) {
        slidingObstacleCells.clear();
        for (const sw of slidingWalls) {
          const path = sw.spec.path;
          const periodSec = sw.spec.period_sec ?? 4;
          if (!path || path.length < 2) {
            // 静止フォールバック
            for (let dy = 0; dy < sw.spec.height; dy++) {
              for (let dx = 0; dx < sw.spec.width; dx++) {
                slidingObstacleCells.add(`${sw.spec.x + dx},${sw.spec.y + dy}`);
              }
            }
            continue;
          }
          const nowMs = performance.now();
          const elapsed = (nowMs - sw.startMs) / 1000;
          const totalPeriod = periodSec;
          const t = (elapsed % totalPeriod) / totalPeriod; // 0..1
          const segments = path.length - 1;
          const segT = t * segments;
          const segIdx = Math.min(segments - 1, Math.floor(segT));
          const segFrac = segT - segIdx;
          const p0 = path[segIdx]!;
          const p1 = path[segIdx + 1]!;
          const nx = p0.x + (p1.x - p0.x) * segFrac;
          const ny = p0.y + (p1.y - p0.y) * segFrac;
          sw.currentX = nx;
          sw.currentY = ny;

          // 占有セル(整数化)
          const ix = Math.round(nx);
          const iy = Math.round(ny);
          for (let dy = 0; dy < sw.spec.height; dy++) {
            for (let dx = 0; dx < sw.spec.width; dx++) {
              slidingObstacleCells.add(`${ix + dx},${iy + dy}`);
            }
          }

          // 再描画
          sw.gfx.clear();
          sw.gfx.roundRect(
            nx * cellPx + 2, ny * cellPx + 2,
            sw.spec.width * cellPx - 4, sw.spec.height * cellPx - 4,
            Math.max(4, cellPx * 0.18),
          );
          sw.gfx.fill({ color: 0x4a3828 });
          sw.gfx.stroke({ width: 2.5, color: 0x221408, alpha: 0.85 });
          // 影(動いている感)
          sw.gfx.roundRect(
            nx * cellPx + 4, ny * cellPx + 5,
            sw.spec.width * cellPx - 8, sw.spec.height * cellPx - 8,
            Math.max(3, cellPx * 0.14),
          );
          sw.gfx.stroke({ width: 1, color: 0x000000, alpha: 0.22 });
        }
      }

      // === G2: Color Gate 更新 ===
      if (colorGateSpecs.length > 0) {
        updateColorGates(territory.coverage());
      }

      // === Obstacle 進入判定 — 新マス進入時のみ(静的壁 + sliding wall + color gate) ===
      if (playerEntered) {
        const key = `${playerEntered.x},${playerEntered.y}`;
        if (
          (obstacleCells.size > 0 && obstacleCells.has(key)) ||
          (slidingObstacleCells.size > 0 && slidingObstacleCells.has(key))
        ) {
          handleFailure('壁にぶつかった!');
          return;
        }
      }

      // === Collectible 取得判定 — 新マス進入時のみ ===
      if (playerEntered && collectibles.size > 0) {
        const ckey = `${playerEntered.x},${playerEntered.y}`;
        const item = collectibles.get(ckey);
        if (item) {
          collectibleLayer.removeChild(item.gfx);
          item.gfx.destroy();
          collectibles.delete(ckey);
          switch (item.info.type) {
            case 'color_bonus':
            case 'heart_bonus':
              this.onCollectibleGainedCb?.(item.info.type, item.info.value ?? 0);
              break;
            case 'time_crystal':
              if (this.currentTimeLimitSec !== null) {
                this.currentTimeLimitSec += 10;
              }
              this.onTimeCrystalGainedCb?.();
              break;
            case 'shield_bubble':
              this.playerHasShield = true;
              this.updateHelperBadges();
              break;
            case 'coverage_token': {
              const tx = item.info.x - 1;
              const ty = item.info.y - 1;
              territory.setRect(Math.max(0, tx), Math.max(0, ty), 3, 3);
              const cov = territory.coverage();
              this.checkMilestone(cov);
              if (checkWinAfterCoverageUpdate(cov)) {
                showResult(cov, stageJson.rewards.color_base);
                return;
              }
              break;
            }
            case 'quick_dash':
              this.playerDashUntilMs = now + 2500;
              break;
            case 'art_note':
              showArtNoteToast(getArtNote(item.info.note_id ?? ''));
              break;
            case 'hourglass':
              // G5: +30 秒
              if (this.currentTimeLimitSec !== null) {
                this.currentTimeLimitSec += 30;
              }
              showArtNoteToast('+30 秒');
              break;
            case 'magnet_pigment': {
              // G6: Chebyshev 距離 6 以内の color_bonus を自動収集
              const pPos = playerEntered;
              const toCollect: string[] = [];
              for (const [ck, ci] of collectibles) {
                if (ci.info.type !== 'color_bonus') continue;
                const dist = Math.max(Math.abs(ci.info.x - pPos.x), Math.abs(ci.info.y - pPos.y));
                if (dist <= 6) toCollect.push(ck);
              }
              for (const ck of toCollect) {
                const ci = collectibles.get(ck);
                if (!ci) continue;
                collectibleLayer.removeChild(ci.gfx);
                ci.gfx.destroy();
                collectibles.delete(ck);
                this.onCollectibleGainedCb?.('color_bonus', ci.info.value ?? 15);
              }
              showArtNoteToast(`磁石: +${toCollect.length} 個収集`);
              break;
            }
            case 'painting_fragment': {
              // G7: 名画ピース
              if (this.paintingFragmentStore) {
                const result = this.paintingFragmentStore.add(stageJson.chapter, 1);
                if (result.completed) {
                  showArtNoteToast(`第 ${stageJson.chapter} 章の名画完成!`);
                } else {
                  showArtNoteToast(`絵画ピース +1 (${result.newCount}/100)`);
                }
              } else {
                showArtNoteToast('絵画ピース +1');
              }
              break;
            }
          }
        }
      }

      // Freeze Brush ヘルパー有効中は敵を完全停止
      const enemiesFrozen = this.enemiesFreezeUntilMs > now;
      // 敵側にも special_zone を適用
      for (const enemy of enemies) {
        if (enemiesFrozen) continue;
        let enemyFactor = 1.0;
        let eWindDx = 0;
        let eWindDy = 0;
        if (zones.length > 0) {
          const ep = enemy.getGridPos();
          for (const z of zones) {
            if (
              ep.x >= z.x &&
              ep.x < z.x + z.width &&
              ep.y >= z.y &&
              ep.y < z.y + z.height
            ) {
              switch (z.type) {
                case 'speed_boost':
                case 'slow_zone':
                case 'wet_paint':
                case 'honey_trap':
                  enemyFactor *= z.multiplier ?? 1.0;
                  break;
                case 'wind_stream': {
                  const d = z.direction ?? 'right';
                  if (d === 'right') eWindDx += 0.6;
                  else if (d === 'left') eWindDx -= 0.6;
                  else if (d === 'down') eWindDy += 0.6;
                  else eWindDy -= 0.6;
                  break;
                }
              }
            }
          }
        }
        // wind stream の視覚的ナッジ(enemy.view を直接ずらす演出のみ、grid 位置は変えない)
        if (eWindDx !== 0 || eWindDy !== 0) {
          enemy.view.x += eWindDx * cellPx * dt;
          enemy.view.y += eWindDy * cellPx * dt;
        }
        enemy.update(dt * enemyFactor);
      }
      trail.setVisualEndpoint(player.view.x, player.view.y);
      stepCamera(dt);
      stepPlayerRotation(dt);

      // ミニ canvas を 4 フレームに 1 回更新(60fps → 15fps、CPU 負担を抑える)
      miniFrameCounter = (miniFrameCounter + 1) % 4;
      if (miniFrameCounter === 0) renderMiniCanvas();

      // wind stream: player view の視覚ナッジ
      if (windDx !== 0 || windDy !== 0) {
        player.view.x += windDx * cellPx * dt;
        player.view.y += windDy * cellPx * dt;
      }

      if (playerEntered) {
        const inOwnTerritory = territory.contains(playerEntered);
        if (!inOwnTerritory) {
          trail.add(playerEntered);
          // wide brush: 隣接 4 セルも trail に追加
          if (playerWideBrushUntilMs > now) {
            const neighbors = [
              { x: playerEntered.x - 1, y: playerEntered.y },
              { x: playerEntered.x + 1, y: playerEntered.y },
              { x: playerEntered.x, y: playerEntered.y - 1 },
              { x: playerEntered.x, y: playerEntered.y + 1 },
            ];
            for (const nb of neighbors) {
              if (!territory.contains(nb) && !trail.contains(nb)) {
                trail.add(nb);
              }
            }
          }
          // mirror_canvas: zone 中央 X 軸対称セルにも trail.add
          if (inMirrorCanvas && mirrorZone) {
            const mirrorY = mirrorZone.y + mirrorZone.height - 1 - (playerEntered.y - mirrorZone.y);
            const mirrorPos = { x: playerEntered.x, y: mirrorY };
            if (
              mirrorPos.y !== playerEntered.y &&
              !territory.contains(mirrorPos) &&
              !trail.contains(mirrorPos)
            ) {
              trail.add(mirrorPos);
            }
          }
          // Chapter 2 G1 Ripple Zone: 4 隣接セル(上下左右)も trail.add(波紋効果)
          if (inRippleZone) {
            const rippleNeighbors = [
              { x: playerEntered.x + 1, y: playerEntered.y },
              { x: playerEntered.x - 1, y: playerEntered.y },
              { x: playerEntered.x, y: playerEntered.y + 1 },
              { x: playerEntered.x, y: playerEntered.y - 1 },
            ];
            for (const nb of rippleNeighbors) {
              if (
                nb.x >= 0 && nb.x < worldGrid &&
                nb.y >= 0 && nb.y < worldGrid &&
                !territory.contains(nb) &&
                !trail.contains(nb)
              ) {
                trail.add(nb);
              }
            }
          }
        } else if (!trail.isEmpty()) {
          const trailCells = trail.getCells();
          const enclosed = territory.enclose(trailCells);
          const lastTrailCell = trailCells[trailCells.length - 1] ?? playerEntered;
          territory.addCells(enclosed, lastTrailCell);
          // paper.io 式:囲んだ/通過したマスがライバル領地に属していたら奪取。
          // enclose() はライバルセルを「空きマス」扱いするため enclosed に含まれる。
          let stolenCount = 0;
          for (const rival of this.rivalTerritories) {
            for (const cell of enclosed) {
              if (rival.contains(cell)) stolenCount++;
            }
            rival.removeCells(enclosed);
          }
          // Task D: 奪取演出
          if (stolenCount > 0) {
            this.showStealPopup(stolenCount);
          }

          // Lot 1: 面積ボーナス倍率
          const cellCount = enclosed.length;
          const loopMultiplier =
            cellCount >= 61 ? 3.0 :
            cellCount >= 31 ? 2.0 :
            cellCount >= 11 ? 1.5 : 1.0;

          // Task B: コンボ倍率
          if (now < this.comboExpireMs) {
            this.comboCount = Math.min(this.comboCount + 1, 4);
          } else {
            this.comboCount = 1;
          }
          this.comboExpireMs = now + 3500;
          const comboMultiplier =
            this.comboCount >= 4 ? 1.6 :
            this.comboCount === 3 ? 1.4 :
            this.comboCount === 2 ? 1.2 : 1.0;
          const finalMultiplier = loopMultiplier * comboMultiplier;

          // HUD コンボ表示更新
          if (this.hudCombo) {
            if (this.comboCount >= 2) {
              this.hudCombo.textContent = `COMBO ×${this.comboCount}`;
              this.hudCombo.classList.add('is-active');
            } else {
              this.hudCombo.textContent = '';
              this.hudCombo.classList.remove('is-active');
            }
          }

          this.onLoopBonusCb?.(cellCount, finalMultiplier);

          // Lot 1: 面積比例バースト強度
          const burstIntensity = Math.min(1, cellCount / 80);
          loopBurst.trigger(enclosed, burstIntensity);
          window.__ck_sfx?.playSfx('enclose_burst');

          // Lot 1: ループボーナスポップアップ(コンボ情報も含む)
          const totalCells = worldGrid * worldGrid;
          const pctGain = (cellCount / totalCells) * 100;
          this.showLoopBonusPopup(pctGain, finalMultiplier, this.comboCount);

          trail.clear();
          const cov = territory.coverage();
          this.checkMilestone(cov); // B2: 25/50/75% でパルス
          if (checkWinAfterCoverageUpdate(cov)) {
            // Pixi WinOverlay は使わず、HTML の Result Overlay を表示(Royal-Match 風)
            showResult(cov, stageJson.rewards.color_base);
            return;
          }
        }
      }

      const playerPos = player.getGridPos();
      const dashActive = this.playerDashUntilMs > now;
      for (const enemy of enemies) {
        const enemyPos = enemy.getGridPos();
        if (!dashActive && gridEquals(playerPos, enemyPos)) {
          handleFailure('敵に接触!');
          return;
        }
        if (!dashActive && trail.contains(enemyPos)) {
          // Task E: トレイル切断視覚 FX
          this.triggerTrailCutFx(trail.getCells(), enemyPos, world, grid);
          window.__ck_sfx?.playSfx('trail_cut');
          handleFailure('敵にトレイルを切られた!');
          return;
        }
      }
    };
    this.tickerCallback = tickerCallback;
    app.ticker.add(tickerCallback);

    // C3: ウィンドウリサイズで Pixi renderer も追従、camera は computeCameraTarget が
    // 毎フレーム app.renderer.width/height を読むので自動で再計算される。
    const handleResize = (): void => {
      if (!stageEl || !this.app) return;
      const box = stageEl.getBoundingClientRect();
      const w = Math.max(300, Math.round(box.width));
      const h = Math.max(300, Math.round(box.height));
      if (this.app.renderer.width !== w || this.app.renderer.height !== h) {
        this.app.renderer.resize(w, h);
      }
    };
    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);
    this.detachResize = (): void => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
    };

    snapCamera();
    this.updateHud(territory.coverage(), stageState.getLives());
  }

  /** ステージ開始時タイトルカード(1.8 秒で自動消滅、reduced-motion 時はアニメ短縮)。
   *  M1: スローイン完了(SLOW_IN_DURATION_SEC 後)に DRAW! キューを表示し、1 秒後に除去。 */
  private showStageCard(stageId: string, title: string): void {
    if (!this.stageCard) return;
    const numEl = this.stageCard.querySelector<HTMLElement>('[data-stage-card-num]');
    const nameEl = this.stageCard.querySelector<HTMLElement>('[data-stage-card-name]');
    if (numEl) numEl.textContent = `STAGE ${stageId}`;
    if (nameEl) nameEl.textContent = title;

    // クリーンアップ(連続表示時の重複防止)
    if (this.stageCardTimeout !== null) {
      window.clearTimeout(this.stageCardTimeout);
      this.stageCardTimeout = null;
    }
    this.stageCard.hidden = false;
    // CSS アニメをリスタートするため一旦クラス除去 → 再追加
    this.stageCard.classList.remove('is-visible');
    // 強制リフロー(transform/opacity の transition を再発火)
    void this.stageCard.offsetWidth;
    this.stageCard.classList.add('is-visible');

    this.stageCardTimeout = window.setTimeout(() => {
      this.stageCard?.classList.remove('is-visible');
      window.setTimeout(() => {
        if (this.stageCard) this.stageCard.hidden = true;
      }, 240);
      this.stageCardTimeout = null;
    }, 1800);

    // M1: DRAW! キュー — スローイン完了タイミングで is-visible を付与。
    // 残留防止: 先行タイマーをキャンセルし、既存の is-visible を除去してからスケジュール。
    if (this.stageDrawCueTimeout !== null) {
      window.clearTimeout(this.stageDrawCueTimeout);
      this.stageDrawCueTimeout = null;
    }
    this.stageDrawCue?.classList.remove('is-visible');

    this.stageDrawCueTimeout = window.setTimeout(() => {
      this.stageDrawCue?.classList.add('is-visible');
      // CSS アニメは 0.7 秒 forwards。1 秒後にクラスを除去して次ステージで再利用可能にする。
      this.stageDrawCueTimeout = window.setTimeout(() => {
        this.stageDrawCue?.classList.remove('is-visible');
        this.stageDrawCueTimeout = null;
      }, 1000);
    }, SLOW_IN_DURATION_SEC * 1000);
  }

  /** B2: マイルストーン超過チェック。
   *  「目標到達度(coverage / target)」が 25/50/75% を越えるごとに Paint ピルをパルス。 */
  private checkMilestone(coverage: number): void {
    const target = this.currentTargetCoverage;
    const ratio = target > 0 ? coverage / target : 0;
    while (
      this.lastMilestoneIndex < COVERAGE_MILESTONES.length &&
      ratio >= COVERAGE_MILESTONES[this.lastMilestoneIndex]!
    ) {
      this.lastMilestoneIndex++;
      const pill = this.root.querySelector<HTMLElement>('.hud-pill--paint');
      if (pill) {
        pill.classList.remove('is-pulse');
        // 次フレームで再追加してアニメをリスタート
        void pill.offsetWidth;
        pill.classList.add('is-pulse');
        window.setTimeout(() => pill.classList.remove('is-pulse'), MILESTONE_PULSE_MS);
      }
    }
  }

  async stop(): Promise<void> {
    if (!this.app) return;
    if (this.tickerCallback) {
      this.app.ticker.remove(this.tickerCallback);
      this.tickerCallback = null;
    }
    this.detachKeyboard?.();
    this.detachPointer?.();
    this.detachResize?.();
    this.detachKeyboard = null;
    this.detachPointer = null;
    this.detachResize = null;
    this.pauseRequest = null;
    this.resumeRequest = null;
    this.helperGameplayActive = false;
    this.helperBurstAction = null;
    if (this.stageCardTimeout !== null) {
      window.clearTimeout(this.stageCardTimeout);
      this.stageCardTimeout = null;
    }
    if (this.stageCard) {
      this.stageCard.classList.remove('is-visible');
      this.stageCard.hidden = true;
    }
    // M1: DRAW! キュー後始末
    if (this.stageDrawCueTimeout !== null) {
      window.clearTimeout(this.stageDrawCueTimeout);
      this.stageDrawCueTimeout = null;
    }
    this.stageDrawCue?.classList.remove('is-visible');
    this.togglePauseMenu(false);
    this.rivalTerritories = [];
    this.app.destroy(true, { children: true, texture: true });
    this.app = null;
    const stageEl = this.root.querySelector<HTMLDivElement>('[data-stage]');
    if (stageEl) stageEl.innerHTML = '';
  }

  private updateHud(coverage: number, _lives?: number): void {
    void _lives; // 旧 in-run lives は HUD には表示しない(Hearts エナジーに統一)
    const target = this.currentTargetCoverage;
    if (this.hudPaint) {
      this.hudPaint.textContent = `${Math.round(coverage * 100)}%`;
    }
    if (this.hudTarget) {
      this.hudTarget.textContent = `${Math.round(target * 100)}%`;
    }
    if (this.hudProgressFill) {
      const ratio = target > 0 ? Math.min(1, coverage / target) : 0;
      this.hudProgressFill.style.width = `${(ratio * 100).toFixed(1)}%`;
      this.hudProgressFill.classList.toggle('is-complete', ratio >= 1);
    }
    if (this.hudTime) {
      let displaySec: number;
      let isWarning = false;
      if (this.currentTimeLimitSec !== null) {
        // カウントダウン: 残り時間表示。残 10 秒以下で警告状態。
        const remaining = Math.max(0, this.currentTimeLimitSec - this.elapsedSec);
        displaySec = Math.ceil(remaining);
        isWarning = remaining > 0 && remaining < 10;
      } else {
        // 旧動作: 経過時間カウントアップ
        displaySec = Math.max(0, Math.floor(this.elapsedSec));
      }
      const mm = Math.floor(displaySec / 60).toString().padStart(2, '0');
      const ss = (displaySec % 60).toString().padStart(2, '0');
      this.hudTime.textContent = `${mm}:${ss}`;
      // pill 全体に警告クラスを切替
      const pill = this.hudTime.closest('.hud-pill');
      pill?.classList.toggle('is-warning', isWarning);
    }
    // ❤️ ピル: Hearts(global energy)を表示。callback 未設定なら未更新。
    if (this.hudLives && this.getHeartsCb) {
      this.hudLives.textContent = String(this.getHeartsCb());
    }
  }

  /** Lot 1: ループ閉鎖時の占領率 % 変化量ポップアップ。
   *  DOM 要素を動的生成して root に追加、0.9 秒で上昇 + フェード。最大 3 個まで同時表示。
   *  Task B: comboCount >= 2 の時はコンボ情報も付記する。 */
  private showLoopBonusPopup(pctGain: number, multiplier: number, comboCount = 1): void {
    if (this.loopBonusPopupCount >= 3) return;
    const el = document.createElement('div');
    el.className = 'loop-bonus-popup';
    const pctText = `+${pctGain.toFixed(1)}%`;
    let text = multiplier > 1.0 ? `${pctText} ×${multiplier.toFixed(1)}!` : pctText;
    if (comboCount >= 2) {
      text += ` COMBO×${comboCount}`;
    }
    el.textContent = text;
    // 画面中央付近にランダムオフセットで配置
    const cx = 40 + Math.random() * 20; // 40〜60% X
    const cy = 45 + Math.random() * 10; // 45〜55% Y
    el.style.left = `${cx}%`;
    el.style.top = `${cy}%`;
    this.root.appendChild(el);
    this.loopBonusPopupCount++;
    // アニメ完了後に DOM 除去
    window.setTimeout(() => {
      el.remove();
      this.loopBonusPopupCount = Math.max(0, this.loopBonusPopupCount - 1);
    }, 950);
  }

  /** Task D: ライバル領地奪取時のポップアップ「+N STOLEN!」(金→赤グラデーション)。 */
  private showStealPopup(stolenCount: number): void {
    const el = document.createElement('div');
    el.className = 'steal-popup';
    el.textContent = `+${stolenCount} STOLEN!`;
    const cx = 38 + Math.random() * 24;
    const cy = 38 + Math.random() * 14;
    el.style.left = `${cx}%`;
    el.style.top = `${cy}%`;
    this.root.appendChild(el);
    window.setTimeout(() => el.remove(), 950);
  }

  /** Task E: トレイル切断瞬間の視覚 FX。
   *  切断点(enemy 位置)に赤い波紋リング + trail の末尾セルから散らばる粒子を 0.6 秒表示。 */
  private triggerTrailCutFx(
    trailCells: readonly import('../../core/types').GridPos[],
    cutPos: import('../../core/types').GridPos,
    world: Container,
    grid: import('../../core/types').GridConfig,
  ): void {
    if (!this.app) return;
    const { cellPx } = grid;
    const g = new Graphics();
    world.addChild(g);

    // 切断点の赤い波紋リング中心
    const cx = (cutPos.x + 0.5) * cellPx;
    const cy = (cutPos.y + 0.5) * cellPx;

    // 散らばる粒子(trail 末尾最大 8 セル)
    const particleCells = trailCells.slice(-8);
    const particles: Array<{ px: number; py: number; vx: number; vy: number }> =
      particleCells.map((c) => ({
        px: (c.x + 0.5) * cellPx,
        py: (c.y + 0.5) * cellPx,
        vx: (Math.random() - 0.5) * cellPx * 4,
        vy: (Math.random() - 0.5) * cellPx * 4 - cellPx,
      }));

    const GRAVITY = cellPx * 6;
    const DURATION = 0.6;
    let age = 0;
    const appRef = this.app;

    const update = (ticker: Ticker): void => {
      const dt = ticker.deltaMS / 1000;
      age += dt;
      if (age >= DURATION) {
        g.destroy();
        if (g.parent) g.parent.removeChild(g);
        appRef.ticker.remove(update);
        return;
      }
      const t = age / DURATION;
      const fade = 1 - t;
      g.clear();

      // 波紋リング(easeOut 拡大)
      const easeOut = 1 - (1 - t) * (1 - t);
      const ringRadius = easeOut * cellPx * 3.5;
      const ringAlpha = Math.pow(fade, 1.2) * 0.75;
      g.circle(cx, cy, ringRadius);
      g.stroke({ width: Math.max(1.5, cellPx * 0.1), color: 0xff2244, alpha: ringAlpha });

      // 粒子(重力付き放射)
      for (const p of particles) {
        p.vx *= 0.96;
        p.vy += GRAVITY * dt;
        p.px += p.vx * dt;
        p.py += p.vy * dt;
        const particleAlpha = Math.pow(fade, 1.4) * 0.9;
        g.circle(p.px, p.py, Math.max(1, cellPx * 0.15));
        g.fill({ color: 0xff2244, alpha: particleAlpha });
      }
    };

    appRef.ticker.add(update);
  }

  /** 浮島(rounded paper)+ shoreline + 紙質グリッド。
   *  Pixi 背景は水色(0x61c6ff)。world 内に島の cream パッチを敷くことで
   *  「水に浮かんだ画用紙」のビジュアルを作る。 */
  private buildPaperGrid(grid: GridConfig): Graphics {
    const g = new Graphics();
    const size = grid.canvasPx;
    const radius = Math.max(16, grid.cellPx * 1.5); // 島の角丸

    // shoreline halo(明るい砂色のリング、ぼかし代わりの 2 段)
    g.roundRect(-12, -12, size + 24, size + 24, radius + 12);
    g.fill({ color: 0xffe2a8, alpha: 0.45 });
    g.roundRect(-6, -6, size + 12, size + 12, radius + 6);
    g.fill({ color: 0xffefc6, alpha: 0.7 });

    // 島本体(cream paper、角丸)
    g.roundRect(0, 0, size, size, radius);
    g.fill({ color: PIXI_COLORS.canvas });

    // 縁の柔らかなアウトライン
    g.roundRect(0, 0, size, size, radius);
    g.stroke({ width: 2, color: 0xc8a85c, alpha: 0.45 });

    // 紙質グリッド(島内のみ)
    for (let i = 0; i <= grid.gridSize; i += 2) {
      g.moveTo(i * grid.cellPx, 0).lineTo(i * grid.cellPx, size);
      g.moveTo(0, i * grid.cellPx).lineTo(size, i * grid.cellPx);
    }
    g.stroke({ width: 0.6, color: PIXI_COLORS.canvasShadow, alpha: 0.55 });

    return g;
  }
}
